// src/components/Loads.js

import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
  useLayoutEffect,
} from "react";

import { useNavigate, useLocation } from "react-router-dom";
import axios from "../axiosConfig";
import { getSocket } from "../lib/socket";
import { getStoredToken, getStoredUser } from "../lib/authStorage";
import { useTranslation } from "react-i18next";

import {
  FaSearch,
  FaChevronDown,
  FaBox,
  FaLayerGroup,
  FaTrashAlt,
  FaUpload,
  FaRegStickyNote,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";

import {
  Truck,
  User,
  Container,
  Building2,
  MapPin,
} from "lucide-react";

import * as CountryFlags from "country-flag-icons/react/3x2";

import "../styles/Loads.css";

const PendentLoads = React.lazy(() => import("./PendentLoads"));
const LoadHistory = React.lazy(() => import("./LoadHistory"));

const TruckSelectorModal = React.lazy(() => import("./TruckSelectorModal"));
const TrailerSelectorModal = React.lazy(() => import("./TrailerSelectorModal"));
const DriverSelectorModal = React.lazy(() => import("./DriverSelectorModal"));

const FreeTextTruckModal = React.lazy(() => import("./FreeTextTruckModal"));
const FreeTextDriverModal = React.lazy(() => import("./FreeTextDriverModal"));
const CMRUploadModal = React.lazy(() => import("./CMRUploadModal"));
const NotesModal = React.lazy(() => import("./NotesModal"));

const TransportSessionLocations = React.lazy(() => import("./TransportSessionLocations"));

// ===== Instant cache (Loads) =====
// ✅ aumenta TTL (ou remove a invalidação no read)
const LOADS_CACHE_TTL_MS = 10 * 60 * 1000; // 10 min (UI instantânea; refresh corrige)
const LOADS_CACHE_PREFIX = "loadevo.loads.v1.";

function safeJsonParse(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function decodePayload(tok) {
  try {
    const b64 = tok.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(b64));
  } catch {
    return null;
  }
}

function resolveCompanyId() {
  try {
    const tok =
      getStoredToken() ||
      localStorage.getItem("authToken") ||
      "";
    const p = tok ? decodePayload(tok) : null;
    const fromJwt = p && (p.company_id ?? p.companyId);
    if (Number.isFinite(Number(fromJwt)) && Number(fromJwt) > 0) return Number(fromJwt);

    const direct =
      sessionStorage.getItem("companyId") ||
      sessionStorage.getItem("company_id") ||
      localStorage.getItem("companyId") ||
      localStorage.getItem("company_id") ||
      null;
    if (direct && Number(direct) > 0) return Number(direct);

    const storedUser = getStoredUser();
    const fromUser =
      storedUser?.company_id ??
      storedUser?.companyId ??
      null;
    if (Number.isFinite(Number(fromUser)) && Number(fromUser) > 0) return Number(fromUser);
  } catch {}
  return null;
}

function loadsCacheKey(cid, kind) {
  const base = cid ? `company:${cid}` : "company:unknown";
  return `${LOADS_CACHE_PREFIX}${base}:${kind}`;
}

// ✅ se estiver stale, DEVOLVEMOS NA MESMA (stale-while-revalidate)
function readLoadsCache(key) {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = safeJsonParse(raw);
    if (!parsed) return null;

    // opcional: marca stale (se quiseres debug)
    const ts = Number(parsed.ts || 0);
    const stale = ts ? (Date.now() - ts > LOADS_CACHE_TTL_MS) : false;

    const data = parsed.data || null;
    if (data && typeof data === "object") {
      return { ...data, __stale: stale };
    }
    return data;
  } catch {
    return null;
  }
}

function writeLoadsCache(key, data) {
  try {
    window.localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
  } catch {
    // ignore
  }
}

// groupedLoads tem Sets (não serializável) -> convertemos p/ arrays e re-hidratamos
function serializeGroupedLoads(grouped = {}) {
  const out = {};
  for (const [sid, s] of Object.entries(grouped || {})) {
    out[sid] = {
      ...s,
      loadBearers: Array.from(s?.loadBearers instanceof Set ? s.loadBearers : []),
    };
  }
  return out;
}

function hydrateGroupedLoads(grouped = {}) {
  const out = {};
  for (const [sid, s] of Object.entries(grouped || {})) {
    out[sid] = {
      ...s,
      loadBearers: new Set(Array.isArray(s?.loadBearers) ? s.loadBearers : []),
    };
  }
  return out;
}

// --- Progress helpers ---
const STAGE_INDEX = {
  PENDING: 0,
  IN_TRANSIT: 1,
  AT_PICKUP: 2,
  AT_DELIVERY: 2,
  COMPLETED: 3,
  CANCELLED: 3,
};
function normalizeStatus(raw) {
  if (!raw) return "PENDING";
  const s = String(raw).toUpperCase().trim();
  if (s.includes("COMPLETED")) return "COMPLETED";
  if (s.includes("CANCELLED")) return "CANCELLED";
  if (
    s.startsWith("AT_PICKUP") ||
    s.includes("AT PICKUP") ||
    s.startsWith("WAITING") ||
    s.startsWith("LOADING")
  ) {
    return "AT_PICKUP";
  }
  if (s.startsWith("AT_DELIVERY") || s.includes("AT DELIVERY")) return "AT_DELIVERY";
  if (s.includes("IN_TRANSIT") || s.includes("MOVING") || s.startsWith("DELAYED")) return "IN_TRANSIT";
  return "PENDING";
}
function pointProgress(pd) {
  const type = pd?.type;
  const status = normalizeStatus(pd?.status);
  if (type === "STRATEGICPOINT") {
    return status === "COMPLETED" || status === "CANCELLED" ? 1 : 0;
  }
  const idx = STAGE_INDEX[status] ?? 0;
  return idx / 3;
}

function hasPendingStrategic(points = []) {
  return (points || []).some(
    (p) =>
      String(p?.type || "").toUpperCase() === "STRATEGICPOINT" &&
      !["COMPLETED", "CANCELLED"].includes(normalizeStatus(p?.status))
  );
}

function computeSessionProgressFromPoints(points = []) {
  const valid = points.filter(Boolean);
  if (!valid.length) return 0;
  const sum = valid.reduce((acc, pd) => acc + pointProgress(pd), 0);
  return Math.round((sum / valid.length) * 100);
}
function getUniqueTruckCount(deliveries = [], type) {
  const filtered = deliveries.filter((d) => d.type === type && d.truck && d.truck.plate);
  const uniquePlates = new Set(filtered.map((d) => d.truck.plate));
  return uniquePlates.size;
}
function getAllPickups(loads, loadId) {
  const load = loads.find((l) => l.id === loadId);
  return load?.pickupDeliveries?.filter((pd) => pd.type === "PICKUP") || [];
}
function getAllDeliveries(loads, loadId) {
  const load = loads.find((l) => l.id === loadId);
  return load?.pickupDeliveries?.filter((pd) => pd.type === "DELIVERY") || [];
}
// ==== Prefill helpers para o Details ====
function pdToPoint(pd = {}) {
  const place = pd.place || {};
  return {
    placeId: pd.placeId ?? place.id ?? null,

    location: pd.location ?? place.placeName ?? "",
    date: pd.date ?? pd.datetime ?? null,
    hour: pd.hour ?? "",

    // ✅ Reference (PD-level)
    reference: pd.reference ?? "",

    address: pd.address ?? place.address ?? "",
    postalCode: pd.postalCode ?? place.postalCode ?? "",
    city: pd.city ?? place.city ?? "",
    country: pd.country ?? place.country ?? "",
    gMapsLink: pd.gMapsLink ?? place.gMapsLink ?? "",
    coordinates: pd.coordinates ?? place.coordinates ?? "",
    contactPhone: pd.contactPhone ?? place.contactPhone ?? "",
    workingHours: pd.workingHours ?? place.workingHours ?? null,

    loadingAccess: pd.loadingAccess ?? place.loadingAccess ?? null,
    handlingEquipment: pd.handlingEquipment ?? place.handlingEquipment ?? null,
  };
}

function buildPointsFromPDs(pds = [], type) {
  return (pds || [])
    .filter((pd) => String(pd?.type).toUpperCase() === type)
    .map(pdToPoint);
}
function extractDimsFull(src = {}) {
  return {
    length:
      src.length ??
      src.totalLength ??
      src.dimensions?.length ??
      src.size?.length ??
      src.merchandise?.length ??
      null,
    height: src.height ?? src.dimensions?.height ?? src.size?.height ?? src.merchandise?.height ?? null,
    width: src.width ?? src.dimensions?.width ?? src.size?.width ?? src.merchandise?.width ?? null,
  };
}
function normalizeLoadForPrefill(row = {}, api = {}) {
  const merged = { ...row, ...api };

  const pickupPoints =
    Array.isArray(merged.pickupPoints) && merged.pickupPoints.length
      ? merged.pickupPoints
      : buildPointsFromPDs(merged.pickupDeliveries, "PICKUP");

  const deliveryPoints =
    Array.isArray(merged.deliveryPoints) && merged.deliveryPoints.length
      ? merged.deliveryPoints
      : buildPointsFromPDs(merged.pickupDeliveries, "DELIVERY");

  const dims = extractDimsFull(merged);

  const carrierId = merged.carrierId ?? merged.loadsAsCarrier?.id ?? merged.carrier?.id ?? null;

  const carrierName =
    merged.carrierName ?? merged.loadsAsCarrier?.companyName ?? merged.carrier?.companyName ?? null;

  // uiCounter/loadNumber/coisas de sessão
  const uiCounter = merged.loadNumber ?? merged.uiCounter ?? merged.id ?? null;
  const tsUiCounter = merged.transportSession?.transportSessionNumber ?? merged.tsUiCounter ?? null;

  return {
    ...merged,
    pickupPoints,
    deliveryPoints,
    ...dims,
    carrierId,
    carrierName,
    uiCounter,
    tsUiCounter,
    price: merged.price ?? null,
  };
}

const uniq = (arr) => [...new Set(arr.filter(Boolean).map((s) => String(s).trim()))];

function platesFromPoints(points = [], loadId, type) {
  const sameType = points.filter((p) => p.loadId === loadId && String(p.type).toUpperCase() === type);

  // ordena por seq; se não houver, usa date+hour
  const sorted = sameType.slice().sort((a, b) => {
    const key = (x) => {
      if (x.seq != null) return Number(x.seq);
      const hhmm = (x.hour || "00:00").padStart(5, "0");
      return new Date(`${x.date || "1970-01-01"}T${hhmm}:00`).getTime();
    };
    return key(a) - key(b);
  });

  return uniq(
    sorted.map((p) => p.truckPlate || p.truckArrivalPlate || p.truckDeparturePlate || p?.truck?.plate)
  );
}

function trailerPlatesFromPoints(points = [], loadId, type) {
  const sameType = points.filter((p) => p.loadId === loadId && String(p.type).toUpperCase() === type);

  const sorted = sameType.slice().sort((a, b) => {
    const key = (x) => {
      if (x.seq != null) return Number(x.seq);
      const hhmm = (x.hour || "00:00").padStart(5, "0");
      return new Date(`${x.date || "1970-01-01"}T${hhmm}:00`).getTime();
    };
    return key(a) - key(b);
  });

  return uniq(
    sorted.map(
      (p) =>
        p.trailerPlate ||
        p.trailerArrivalPlate ||
        p.trailerDeparturePlate ||
        p?.trailer?.plate ||
        p?.trailer?.trailerPlate ||
        p?.trailer?.trailerNumber ||
        null
    )
  );
}

/**
 * Driver names únicos para um loadId + tipo (PICKUP / DELIVERY),
 * tal como fazemos para os trucks.
 */
function driverNamesFromPoints(points = [], loadId, type) {
  const sameType = points.filter((p) => p.loadId === loadId && String(p.type).toUpperCase() === type);

  const sorted = sameType.slice().sort((a, b) => {
    const key = (x) => {
      if (x.seq != null) return Number(x.seq);
      const hhmm = (x.hour || "00:00").padStart(5, "0");
      return new Date(`${x.date || "1970-01-01"}T${hhmm}:00`).getTime();
    };
    return key(a) - key(b);
  });

  return uniq(
    sorted.flatMap((p) => [
      p.driverName,
      p.driver2Name,
      p.driverNameArrival,
      p.driverNameDeparture,
      p?.driver?.fullname,
      p?.driver?.nickname,
      p?.secondDriver?.fullname,
      p?.secondDriver?.nickname,
    ])
  );
}

function countPointsOfType(points = [], loadId, type) {
  return points.filter((p) => p.loadId === loadId && String(p.type).toUpperCase() === type).length;
}

function hasAssignedTruckBulk(points = [], load = {}, type = "PICKUP") {
  const typeUpper = String(type || "").toUpperCase();

  if (Array.isArray(points) && points.length > 0) {
    return points.some(
      (p) =>
        Number(p.loadId) === Number(load?.id) &&
        String(p.type || "").toUpperCase() === typeUpper &&
        !!(
          p?.truckId ||
          String(
            p?.truckPlate ||
              p?.truckArrivalPlate ||
              p?.truckDeparturePlate ||
              p?.truck?.plate ||
              ""
          ).trim()
        )
    );
  }

  if (typeUpper === "DELIVERY") {
    return !!(
      load?.deliveryTruck?.plate ||
      load?.truckDeliveryInfo ||
      load?.truckIdDelivery
    );
  }

  return !!(
    load?.pickupTruck?.plate ||
    load?.truckPickupInfo ||
    load?.truckIdPickup
  );
}

function hasAssignedTrailerBulk(points = [], load = {}, type = "PICKUP") {
  const typeUpper = String(type || "").toUpperCase();

  if (Array.isArray(points) && points.length > 0) {
    return points.some(
      (p) =>
        Number(p.loadId) === Number(load?.id) &&
        String(p.type || "").toUpperCase() === typeUpper &&
        !!(
          p?.trailerId ||
          String(
            p?.trailerPlate ||
              p?.trailerArrivalPlate ||
              p?.trailerDeparturePlate ||
              p?.trailer?.plate ||
              p?.trailer?.trailerNumber ||
              ""
          ).trim()
        )
    );
  }

  if (typeUpper === "DELIVERY") {
    return !!(
      load?.deliveryTrailer?.plate ||
      load?.trailerDeliveryInfo ||
      load?.trailerIdDelivery
    );
  }

  return !!(
    load?.pickupTrailer?.plate ||
    load?.trailerPickupInfo ||
    load?.trailerIdPickup
  );
}

function hasAssignedDriverBulk(points = [], load = {}, type = "PICKUP") {
  const typeUpper = String(type || "").toUpperCase();

  if (Array.isArray(points) && points.length > 0) {
    return points.some(
      (p) =>
        Number(p.loadId) === Number(load?.id) &&
        String(p.type || "").toUpperCase() === typeUpper &&
        !!(
          p?.driverId ||
          p?.driver2Id ||
          String(
            p?.driverName ||
              p?.driver2Name ||
              p?.driverNameArrival ||
              p?.driverNameDeparture ||
              p?.driver?.fullname ||
              p?.driver?.nickname ||
              p?.secondDriver?.fullname ||
              p?.secondDriver?.nickname ||
              ""
          ).trim()
        )
    );
  }

  if (typeUpper === "DELIVERY") {
    return !!(
      load?.deliveryDriver?.name ||
      load?.driverDeliveryInfo ||
      load?.driverIdDelivery
    );
  }

  return !!(
    load?.pickupDriver?.name ||
    load?.driverPickupInfo ||
    load?.driverIdPickup
  );
}

function normalizeLoadPatchFromSocket(payload = {}) {
  const raw = payload?.load || payload || {};
  if (!raw?.id) return null;

  const patch = { ...raw };

  const hasOwn = (obj, key) =>
    Object.prototype.hasOwnProperty.call(obj || {}, key);

  const cleanLabel = (value) => {
    const s = String(value || "").trim();
    if (!s) return null;
    return s.split("•")[0].trim() || null;
  };

  // 🚚 Truck pickup
  if (hasOwn(patch, "truckPickupInfo") || hasOwn(patch, "truckIdPickup")) {
    const plate = cleanLabel(patch.truckPickupInfo);

    patch.pickupTruck = plate
      ? {
          ...(patch.pickupTruck || {}),
          plate,
        }
      : null;
  }

  // 🚚 Truck delivery
  if (hasOwn(patch, "truckDeliveryInfo") || hasOwn(patch, "truckIdDelivery")) {
    const plate = cleanLabel(patch.truckDeliveryInfo);

    patch.deliveryTruck = plate
      ? {
          ...(patch.deliveryTruck || {}),
          plate,
        }
      : null;
  }

  // 🛻 Trailer pickup
  if (hasOwn(patch, "trailerPickupInfo") || hasOwn(patch, "trailerIdPickup")) {
    const plate = cleanLabel(patch.trailerPickupInfo);

    patch.pickupTrailer = plate
      ? {
          ...(patch.pickupTrailer || {}),
          plate,
        }
      : null;
  }

  // 🛻 Trailer delivery
  if (hasOwn(patch, "trailerDeliveryInfo") || hasOwn(patch, "trailerIdDelivery")) {
    const plate = cleanLabel(patch.trailerDeliveryInfo);

    patch.deliveryTrailer = plate
      ? {
          ...(patch.deliveryTrailer || {}),
          plate,
        }
      : null;
  }

  // 🧑‍✈️ Driver pickup
  if (hasOwn(patch, "driverPickupInfo") || hasOwn(patch, "driverIdPickup")) {
    const name = cleanLabel(patch.driverPickupInfo);

    patch.pickupDriver = name
      ? {
          ...(patch.pickupDriver || {}),
          name,
        }
      : null;
  }

  // 🧑‍✈️ Driver delivery
  if (hasOwn(patch, "driverDeliveryInfo") || hasOwn(patch, "driverIdDelivery")) {
    const name = cleanLabel(patch.driverDeliveryInfo);

    patch.deliveryDriver = name
      ? {
          ...(patch.deliveryDriver || {}),
          name,
        }
      : null;
  }

  return patch;
}

// --- Short-cache for session points (plates) ---
const SESSION_POINTS_TTL_MS = 30_000; // 30s

/**
 * Bulk-fetch PDs for sessions, with a short TTL cache.
 * - Seeds the UI with any cached sessions immediately
 * - Fetches only stale/missing sessions
 * - Merges results into sessionPoints
 */
  async function primeAndFetchSessionPoints(
    sessionIds,
    axios,
    setSessionPoints,
    cacheRef,
    inflightRef,
    onFetched // (sid, rows) => void
  ) {
  if (!Array.isArray(sessionIds) || sessionIds.length === 0) return;

  const now = Date.now();
  const cachedForUi = {};
  const toFetch = [];

  // 1) Use fresh cache immediately, select stale/missing for fetch
  for (const sid of sessionIds) {
    const entry = cacheRef.current[sid];
    if (entry && now - entry.ts < SESSION_POINTS_TTL_MS && Array.isArray(entry.rows)) {
      cachedForUi[sid] = entry.rows;
    } else {
      toFetch.push(String(sid));
    }
  }

  // 2) Push cached into UI right away (no flicker)
  if (Object.keys(cachedForUi).length) {
    setSessionPoints((prev) => ({ ...prev, ...cachedForUi }));
  }

  // 3) Nothing to fetch? we're done
  if (toFetch.length === 0) return;

  // 4) Deduplicate in-flight requests
  const key = toFetch.sort().join(",");
  if (inflightRef.current.has(key)) return; // already fetching this set
  inflightRef.current.add(key);

  try {
    const { data } = await axios.get("/pickupdeliveries/by-sessions", {
      params: { ids: toFetch.join(",") },
    });

    // Normalize plates and update cache + UI
    const next = {};
    for (const [sid, rows] of Object.entries(data || {})) {
      const normalized = (rows || []).map((p) => {
        const truckPlate =
          p.truckPlate ??
          p?.truck?.plate ??
          p?.truckArrival?.plate ??
          p?.truckDeparture?.plate ??
          null;

        const trailerPlate =
          p.trailerPlate ??
          p?.trailer?.plate ??
          p?.trailer?.trailerPlate ??
          p?.trailer?.trailerNumber ??
          p?.trailerArrival?.plate ??
          p?.trailerArrival?.trailerPlate ??
          p?.trailerArrival?.trailerNumber ??
          p?.trailerDeparture?.plate ??
          p?.trailerDeparture?.trailerPlate ??
          p?.trailerDeparture?.trailerNumber ??
          null;

        return {
          ...p,

          // trucks
          truckPlate,
          truckArrivalPlate: p.truckArrivalPlate ?? p?.truckArrival?.plate ?? null,
          truckDeparturePlate: p.truckDeparturePlate ?? p?.truckDeparture?.plate ?? null,

          // trailers
          trailerPlate,
          trailerArrivalPlate:
            p.trailerArrivalPlate ??
            p?.trailerArrival?.plate ??
            p?.trailerArrival?.trailerPlate ??
            p?.trailerArrival?.trailerNumber ??
            null,

          trailerDeparturePlate:
            p.trailerDeparturePlate ??
            p?.trailerDeparture?.plate ??
            p?.trailerDeparture?.trailerPlate ??
            p?.trailerDeparture?.trailerNumber ??
            null,
        };
      });

      next[sid] = normalized;
      if (typeof onFetched === "function") {
        try { onFetched(sid, normalized); } catch {}
      }
      cacheRef.current[sid] = { ts: Date.now(), rows: normalized };
    }

    if (Object.keys(next).length) {
      setSessionPoints((prev) => ({ ...prev, ...next }));
    }
  } catch (err) {
    console.warn("Failed to bulk fetch session points:", err);
  } finally {
    inflightRef.current.delete(key);
  }
}

// --- Money helper ---
const formatPrice = (amount, { currency = "EUR", locale = "pt-PT" } = {}) => {
  if (amount == null || amount === "") return "—";
  const num = Number(amount);
  if (Number.isNaN(num)) return String(amount);
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency }).format(num);
  } catch {
    return `${num.toFixed(2)} ${currency}`;
  }
};

function renderLoadTypeBadges(load, t) {
  if (!load) {
    return null;
  }

  const hasAnyBadge =
    load.superImposable ||
    load.loadBearer ||
    load.specialLoad;

  if (!hasAnyBadge) {
    return null;
  }

  return (
    <div className="load-type-badge-floating-center">
      {load.superImposable && (
        <div className="superimposed-badge">
          {t(
            "loads.badges.superImposed"
          )}

          <span className="superimposed-id">
            {t(
              "loads.badges.onLoadBearer",
              {
                id:
                  load.associatedLoadBearerId ||
                  t(
                    "loads.common.na"
                  ),
              }
            )}
          </span>
        </div>
      )}

      {load.loadBearer && (
        <span className="load-bearer-badge">
          {t(
            "loads.badges.loadBearer"
          )}
        </span>
      )}

      {load.specialLoad && (
        <span className="special-load-badge">
          {t(
            "loads.badges.specialLoad"
          )}
        </span>
      )}
    </div>
  );
}

function ResourceChipContent({
  icon: Icon,
  iconClassName = "truck-chip-icon",
  label,
  extraCount = 0,
}) {
  return (
    <span className="truck-label-wrapper">
      <Icon className={iconClassName} size={16} />
      <span className="loads-chip-text">{label}</span>

      {Number(extraCount) > 0 && (
        <span className="loads-resource-badge">
          +{extraCount}
        </span>
      )}
    </span>
  );
}

// Bulk-fetch PDs for many sessions at once, using /pickupdeliveries/by-sessions
async function bulkLoadSessionPoints(sessionIds, axios, setSessionPoints) {
  if (!Array.isArray(sessionIds) || sessionIds.length === 0) return;

  try {
    const { data } = await axios.get("/pickupdeliveries/by-sessions", {
      params: { ids: sessionIds.join(",") },
    });

    // data is an object keyed by transportsessionId -> array of PDs with includes
    const next = {};
    for (const [sid, rows] of Object.entries(data || {})) {
      next[sid] = (rows || []).map((p) => {
        const truckPlate =
          p.truckPlate ??
          p?.truck?.plate ??
          p?.truckArrival?.plate ??
          p?.truckDeparture?.plate ??
          null;

        const trailerPlate =
          p.trailerPlate ??
          p?.trailer?.plate ??
          p?.trailer?.trailerPlate ??
          p?.trailer?.trailerNumber ??
          p?.trailerArrival?.plate ??
          p?.trailerArrival?.trailerPlate ??
          p?.trailerArrival?.trailerNumber ??
          p?.trailerDeparture?.plate ??
          p?.trailerDeparture?.trailerPlate ??
          p?.trailerDeparture?.trailerNumber ??
          null;

        return {
          ...p,

          // trucks
          truckPlate,
          truckArrivalPlate: p.truckArrivalPlate ?? p?.truckArrival?.plate ?? null,
          truckDeparturePlate: p.truckDeparturePlate ?? p?.truckDeparture?.plate ?? null,

          // trailers
          trailerPlate,
          trailerArrivalPlate:
            p.trailerArrivalPlate ??
            p?.trailerArrival?.plate ??
            p?.trailerArrival?.trailerPlate ??
            p?.trailerArrival?.trailerNumber ??
            null,

          trailerDeparturePlate:
            p.trailerDeparturePlate ??
            p?.trailerDeparture?.plate ??
            p?.trailerDeparture?.trailerPlate ??
            p?.trailerDeparture?.trailerNumber ??
            null,
        };
      });
    }

    if (Object.keys(next).length) {
      setSessionPoints((prev) => ({ ...prev, ...next }));
    }
  } catch (err) {
    console.warn("Failed to bulk fetch session points:", err);
  }
}

const MAX_HEIGHT = 2.7;

// ===== Infinite reveal (like Invoices) =====
const LOADS_PAGE_SIZE = 10; // show 10 loads then +10 on scroll
const SESSION_SLIDE_MS = 360;

// --- Carrier helper: determine if the transport carrier is external (≠ company) ---
function getMyCompanyIdSafe() {
  try {
    const direct =
      sessionStorage.getItem("companyId") ||
      sessionStorage.getItem("company_id") ||
      localStorage.getItem("companyId") ||
      localStorage.getItem("company_id") ||
      null;
    if (direct) return direct;

    const storedUser = getStoredUser();
    const fromUser =
      storedUser?.company_id ??
      storedUser?.companyId ??
      null;
    if (fromUser) return fromUser;

    const companyRaw =
      sessionStorage.getItem("company") ||
      localStorage.getItem("company");

    if (companyRaw) {
      const obj = JSON.parse(companyRaw);
      return obj?.id ?? obj?.companyId ?? obj?.company_id ?? null;
    }
  } catch {}
  return null;
}

// ✅ use a function declaration (hoisted) so you can call it anywhere
function dbgTruck(...args) {
  try {
    if (
      sessionStorage.getItem("debugTruckRouting") === "1" ||
      localStorage.getItem("debugTruckRouting") === "1"
    ) {
      console.log(...args);
    }
  } catch {}
}

function isExternalTransportCarrier(load) {
  const myCompanyId = getMyCompanyIdSafe();
  const carrierId =
    load?.carrierId ??
    load?.carrier_id ??
    load?.carrierID ??
    load?.carrier?.id ??
    null;

  if (!myCompanyId) {
    dbgTruck("[Loads][carrierCheck] myCompanyId missing -> treat as INTERNAL (no free-text)", {
      loadId: load?.id,
      carrierId,
      myCompanyId,
    });
    return false;
  }

  if (carrierId == null) {
    dbgTruck("[Loads][carrierCheck] carrierId missing -> treat as INTERNAL (no free-text)", {
      loadId: load?.id,
      carrierId,
      myCompanyId,
    });
    return false;
  }

  const external = String(carrierId) !== String(myCompanyId);
  dbgTruck("[Loads][carrierCheck]", { loadId: load?.id, carrierId, myCompanyId, external });
  return external;
}

  function Loads() {
    useLayoutEffect(() => {
      return () => {};
    }, []);

    const { t, i18n, ready } = useTranslation(["loads", "common"]);

    const priceLocale = useMemo(() => {
      const lang = (i18n.language || "pt").toLowerCase();
      if (lang.startsWith("pt")) return "pt-PT";
      if (lang.startsWith("en")) return "en-GB";
      if (lang.startsWith("es")) return "es-ES";
      if (lang.startsWith("fr")) return "fr-FR";
      if (lang.startsWith("it")) return "it-IT";
      if (lang.startsWith("nl")) return "nl-NL";
      if (lang.startsWith("pl")) return "pl-PL";
      if (lang.startsWith("ja")) return "ja-JP";
      if (lang.startsWith("ko")) return "ko-KR";
      if (lang.startsWith("de")) return "de-DE";
      return "en-GB";
    }, [i18n.language]);

  const navigate = useNavigate();
  const location = useLocation();

  // --- View inicial e sincronização via URL (como no Clients) ---
  const getInitialView = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const v = params.get("view");
    if (["pendent", "current", "history"].includes(v)) return v;
    return "current";
  }, [location.search]);

  const [selectedView, setSelectedView] = useState(getInitialView());

  const focusedLoadId =
    useMemo(
      () => {
        const params =
          new URLSearchParams(
            location.search
          );

        const value =
          Number(
            params.get(
              "focusLoadId"
            )
          );

        return (
          Number.isInteger(
            value
          ) &&
          value >
            0
        )
          ? value
          : null;
      },
      [
        location.search,
      ]
    );

  // ✅ igual ao Drivers: resolve UMA vez, sincrono, e nunca mais muda
  const companyId = useMemo(() => resolveCompanyId(), []);

  const loadsCurrentCacheKey = useMemo(
    () => loadsCacheKey(companyId, "current:summary-with-progress"),
    [companyId]
  );
  const loadsPendentCacheKey = useMemo(
    () => loadsCacheKey(companyId, "pendent:summary"),
    [companyId]
  );

  // ✅ cache lido sync com a cacheKey CERTA (companyId já está resolvido)
  const cacheInit = useMemo(() => ({
    current: readLoadsCache(loadsCurrentCacheKey),
    pendent: readLoadsCache(loadsPendentCacheKey),
  }), [loadsCurrentCacheKey, loadsPendentCacheKey]);

  const [loads, setLoads] = useState(() => Array.isArray(cacheInit.current?.loads) ? cacheInit.current.loads : []);
  const [pendentLoads, setPendentLoads] = useState(() => Array.isArray(cacheInit.pendent?.loads) ? cacheInit.pendent.loads : []);
  const [groupedLoads, setGroupedLoads] = useState(() => hydrateGroupedLoads(cacheInit.current?.groupedLoads || {}));

  // ✅ se temos cache -> tabela aparece logo
  const [baseTableRendered, setBaseTableRendered] = useState(() => !!(cacheInit.current?.loads && cacheInit.current.loads.length));
  const [loadingSessions, setLoadingSessions] = useState({}); // sessionId: boolean

  const [searchTerm, setSearchTerm] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(searchTerm), 120);
    return () => clearTimeout(t);
  }, [searchTerm]);
  const [isFocused, setIsFocused] = useState({ search: false });
  const [errorMessage, setErrorMessage] = useState("");

  const [existingCmrUrls, setExistingCmrUrls] = useState([]);

  const [expandedSessions, setExpandedSessions] = useState({});
  const [openingSessions, setOpeningSessions] = useState({});
  const [closingSessions, setClosingSessions] = useState({});
  const [reloadTransportSessionKey, setReloadTransportSessionKey] = useState(0);

  const [bulkTruckAssignLoad, setBulkTruckAssignLoad] = useState(null);
  const [bulkTruckAssignModalOpen, setBulkTruckAssignModalOpen] = useState(false);

  const [bulkTrailerAssignLoad, setBulkTrailerAssignLoad] = useState(null);
  const [bulkTrailerAssignModalOpen, setBulkTrailerAssignModalOpen] = useState(false);

  // 🆕 Free-text truck modal (for external carriers)
  const [freeTextTruckModalOpen, setFreeTextTruckModalOpen] = useState(false);
  const [freeTextTruckCtx, setFreeTextTruckCtx] = useState(null); 
  // { load, transportsessionId, bulkType: "pickup" | "delivery" }

  // 🆕 Free-text driver modal (for external carriers)
  const [freeTextDriverModalOpen, setFreeTextDriverModalOpen] = useState(false);
  const [freeTextDriverCtx, setFreeTextDriverCtx] = useState(null);
  // { load, transportsessionId, bulkType: "pickup" | "delivery" }

  const [bulkDriverAssignLoad, setBulkDriverAssignLoad] = useState(null);
  const [bulkDriverAssignModalOpen, setBulkDriverAssignModalOpen] = useState(false);
  const [bulkDriverSlot, setBulkDriverSlot] = useState(1);

  const [pendingCmrUploads, setPendingCmrUploads] = useState(new Set());
  const [cmrModalOpen, setCmrModalOpen] = useState(false);
  const [selectedLoadForCmr, setSelectedLoadForCmr] = useState(null);

  // 🆕 guarda o point onde o CMR/fotos pertencem
  const [cmrUploadContext, setCmrUploadContext] = useState(null);
  // { loadId, pickupDeliveryId, pointLabel, pointLocation }

  const [sessionProgress, setSessionProgress] = useState(() => cacheInit.current?.sessionProgress || {});
  const [progressAnimated, setProgressAnimated] = useState({});
  const [sessionPoints, setSessionPoints] = useState({});
  const [activeStrategicSessions, setActiveStrategicSessions] = useState(
    () => new Set(Array.isArray(cacheInit.current?.activeStrategicSessions) ? cacheInit.current.activeStrategicSessions : [])
  );

  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [notesModalLoad, setNotesModalLoad] = useState(null); // { id, uiCounter }
  const [notesDefaultValue, setNotesDefaultValue] = useState("");
  const [pricesHidden, setPricesHidden] = useState(false);

  const scrollRef = useRef(null);

  const focusedLoadHandledRef =
    useRef(
      null
    );

  const expandedRefs = useRef({});
  const expandedSectionRefs = useRef({});
  const expandedInnerRefs = useRef({});

  // ===== Infinite reveal (like Invoices) =====
  const loadsSentinelRef = useRef(null);
  const [visibleRowBudget, setVisibleRowBudget] = useState(LOADS_PAGE_SIZE);

  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);
  const lastBudgetRef = useRef(LOADS_PAGE_SIZE);

  const sessionPointsCacheRef = useRef({}); // sid -> { ts, rows }
  const inflightSessionsRef = useRef(new Set()); // dedupe in-flight calls

  // ✅ Avoid multiple auto-finalize calls for external/subcontracted loads
  const externalFinalizeInFlightRef = useRef(new Set()); // loadId set

  const refreshLoadsInFlightRef = useRef(false);

  const lastRefreshLoadsAtRef = useRef(0);

  const mountedAtRef = useRef(Date.now());

  const shouldIgnoreEarlySocketRefresh = () => {
    return Date.now() - mountedAtRef.current < 2000;
  };

  const sessionPointsRef = useRef(sessionPoints);
  useEffect(() => {
    sessionPointsRef.current = sessionPoints;
  }, [sessionPoints]);

  const maxLength = 13.6;

  const showSuccessToast = (msg) => {
    setToastMessage(msg);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  };

  const applyLoadPatchInstant = useCallback((payload) => {
    const patch = normalizeLoadPatchFromSocket(payload);
    if (!patch?.id) return;

    setLoads((prev) =>
      prev.map((load) =>
        Number(load.id) === Number(patch.id)
          ? {
              ...load,
              ...patch,
            }
          : load
      )
    );

    setGroupedLoads((prev) => {
      const next = { ...prev };

      for (const sid of Object.keys(next)) {
        const session = next[sid];
        if (!Array.isArray(session?.loads)) continue;

        next[sid] = {
          ...session,
          loads: session.loads.map((load) =>
            Number(load.id) === Number(patch.id)
              ? {
                  ...load,
                  ...patch,
                }
              : load
          ),
        };
      }

      return next;
    });
  }, []);

  const applyPickupDeliveryPatchInstant = useCallback((pdPatch = {}) => {
    if (!pdPatch?.id) return;

    const sid =
      pdPatch.sessionId ??
      pdPatch.transportsessionId ??
      pdPatch.transportSessionId ??
      null;

    // 1) Atualiza sessionPoints usado pelos chips da tabela pai
    if (sid) {
      setSessionPoints((prev) => {
        const key = String(sid);
        const rows = Array.isArray(prev?.[key]) ? prev[key] : null;
        if (!rows) return prev;

        const nextRows = rows.map((p) =>
          Number(p.id) === Number(pdPatch.id)
            ? {
                ...p,
                ...pdPatch,
              }
            : p
        );

        sessionPointsCacheRef.current[key] = {
          ts: Date.now(),
          rows: nextRows,
        };

        return {
          ...prev,
          [key]: nextRows,
        };
      });
    }

    // 2) Atualiza pickupDeliveries dentro das loads, se existirem
    setLoads((prev) =>
      prev.map((load) => ({
        ...load,
        pickupDeliveries: Array.isArray(load.pickupDeliveries)
          ? load.pickupDeliveries.map((pd) =>
              Number(pd.id) === Number(pdPatch.id)
                ? {
                    ...pd,
                    ...pdPatch,
                  }
                : pd
            )
          : load.pickupDeliveries,
      }))
    );

    setGroupedLoads((prev) => {
      const next = { ...prev };

      for (const sessionId of Object.keys(next)) {
        const session = next[sessionId];
        if (!Array.isArray(session?.loads)) continue;

        next[sessionId] = {
          ...session,
          loads: session.loads.map((load) => ({
            ...load,
            pickupDeliveries: Array.isArray(load.pickupDeliveries)
              ? load.pickupDeliveries.map((pd) =>
                  Number(pd.id) === Number(pdPatch.id)
                    ? {
                        ...pd,
                        ...pdPatch,
                      }
                    : pd
                )
              : load.pickupDeliveries,
          })),
        };
      }

      return next;
    });
  }, []);

  const openNotesModal = (load) => {
    const server = load?.notes ?? null;
    const existing = (server != null ? server : localStorage.getItem(`loadNote:${load.id}`)) || "";
    setNotesDefaultValue(existing);
    setNotesModalLoad({ id: load.id, uiCounter: load.uiCounter });
    setNotesModalOpen(true);
  };

  const closeNotesModal = () => {
    setNotesModalOpen(false);
    setNotesModalLoad(null);
    setNotesDefaultValue("");
  };

  const saveNotesFromModal = async (text) => {
    if (!notesModalLoad) return;
    try {
      await axios.put(`/loads/${notesModalLoad.id}`, { notes: text ?? "" });
      setLoads((prev) =>
        prev.map((l) => (l.id === notesModalLoad.id ? { ...l, notes: (text ?? "").trim() || null } : l))
      );
      setGroupedLoads((prev) => {
        const next = { ...prev };
        for (const sid of Object.keys(next)) {
          next[sid] = {
            ...next[sid],
            loads: next[sid].loads.map((l) =>
              l.id === notesModalLoad.id ? { ...l, notes: (text ?? "").trim() || null } : l
            ),
          };
        }
        return next;
      });
    } catch (e) {
      console.warn("Failed to save notes to server, keeping local draft:", e);
    } finally {
      if (text) localStorage.setItem(`loadNote:${notesModalLoad.id}`, text);
      else localStorage.removeItem(`loadNote:${notesModalLoad.id}`);
      closeNotesModal();
    }
  };

  // 🔎 Helper de pesquisa simples (para contagens/abas)
  // Works for both Current/History (normalized) and Pendent (denormalized summary)
  const filterLoads = (loadsList, search) => {
    if (!Array.isArray(loadsList)) return [];
    const term = (search || "").trim().toLowerCase();
    if (!term) return loadsList;

    return loadsList.filter((load) => {
      const idMatch = load?.id && String(load.id).toLowerCase().includes(term);

      const pickupText =
        (selectedView === "pendent" ? load?.firstPickupLocation : load?.pickupLocation) || "";
      const deliveryText =
        (selectedView === "pendent" ? load?.firstDeliveryLocation : load?.deliveryLocation) || "";

      const pickupMatch = String(pickupText).toLowerCase().includes(term);
      const deliveryMatch = String(deliveryText).toLowerCase().includes(term);

      return idMatch || pickupMatch || deliveryMatch;
    });
  };

  // Helper para paths aninhados (para o filtro por campos usado na tabela corrente)
  const getFieldValue = (obj, path) => {
    if (!obj || !path) return "";
    const v = path.split(".").reduce((acc, k) => (acc && acc[k] != null ? acc[k] : null), obj);
    return v == null ? "" : String(v);
  };
  const filterByFields = (items, search, fields) => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => fields.some((f) => getFieldValue(it, f).toLowerCase().includes(q)));
  };

  // Campos por vista
  const searchFieldsByView = {
    pendent: ["id", "pendentLoadNumber", "firstPickupLocation", "firstDeliveryLocation", "industryClientName", "carrierClientName", "carrierName"],
    current: ["uiCounter", "id", "transportsessionId", "pickupLocation", "deliveryLocation", "industryClientName", "carrierClientName", "carrierName", "price"],
    history: ["uiCounter", "id", "transportsessionId", "pickupLocation", "deliveryLocation", "industryClientName", "carrierClientName", "carrierName", "price"],
  };

  // ===== Build session entries for CURRENT table (filtered + searchable) =====
  const currentSessionEntries = useMemo(() => {
    if (selectedView !== "current") return [];

    const fieldsForThisView = searchFieldsByView.current || [];

    // mantém a mesma ordem do groupedLoads (Object.entries)
    return Object.entries(groupedLoads || [])
      .map(([transportsessionId, sessionData]) => {
        // 1) filter by view
        const byView = (sessionData?.loads || []).filter((l) => l.status === "current");

        // 2) apply search filter
        const filteredLoads = filterByFields(byView, searchDebounced, fieldsForThisView);

        // keep session visible if strategic points still open
        const shouldRenderSessionShell =
          selectedView === "current" && activeStrategicSessions.has(String(transportsessionId));

        if (filteredLoads.length === 0 && !shouldRenderSessionShell) return null;

        const tsLabel = groupedLoads?.[transportsessionId]?.tsUiCounter ?? transportsessionId;

        // remaining length (como já tinhas)
        const totalNormalLoadLength = filteredLoads
          .filter((load) => !load.superImposable)
          .reduce((acc, load) => acc + parseFloat(load.length || 0), 0);

        const remainingLength = parseFloat((maxLength - totalNormalLoadLength).toFixed(2));

        return {
          transportsessionId: String(transportsessionId),
          sessionData,
          filteredLoads,
          shouldRenderSessionShell,
          remainingLength,
          tsLabel,
        };
      })
      .filter(Boolean);
    }, [
      selectedView,
      groupedLoads,
      searchDebounced,
      activeStrategicSessions,
      maxLength,
    ]);

  function sliceByLoadBudget(entries, budget) {
    const out = [];
    let usedLoads = 0;

    for (const e of entries) {
      const loadsArr = Array.isArray(e.filteredLoads) ? e.filteredLoads : [];

      // mantém sessões estratégicas sem loads visíveis
      if (loadsArr.length === 0) {
        if (e.shouldRenderSessionShell) {
          out.push({ ...e, filteredLoads: [] });
        }
        continue;
      }

      if (usedLoads >= budget) break;

      const remaining = budget - usedLoads;
      const slicedLoads = loadsArr.slice(0, remaining);

      if (slicedLoads.length > 0) {
        out.push({
          ...e,
          filteredLoads: slicedLoads,
        });
        usedLoads += slicedLoads.length;
      }
    }

    return out;
  }

  const visibleCurrentEntries = useMemo(
    () => sliceByLoadBudget(currentSessionEntries, visibleRowBudget),
    [currentSessionEntries, visibleRowBudget]
  );

  useEffect(
    () => {
      if (
        selectedView !==
          "current" ||
        !focusedLoadId
      ) {
        return;
      }

      let requiredBudget =
        0;

      let foundTarget =
        false;

      for (
        const entry of
        currentSessionEntries
      ) {
        const entryLoads =
          Array.isArray(
            entry?.filteredLoads
          )
            ? entry.filteredLoads
            : [];

        for (
          const load of
          entryLoads
        ) {
          requiredBudget +=
            1;

          if (
            Number(
              load?.id
            ) ===
            Number(
              focusedLoadId
            )
          ) {
            foundTarget =
              true;

            break;
          }
        }

        if (
          foundTarget
        ) {
          break;
        }
      }

      if (
        !foundTarget
      ) {
        return;
      }

      setVisibleRowBudget(
        (
          currentBudget
        ) =>
          Math.max(
            currentBudget,
            requiredBudget
          )
      );
    },
    [
      currentSessionEntries,
      focusedLoadId,
      selectedView,
    ]
  );

    useEffect(
    () => {
      if (
        selectedView !==
          "current" ||
        !focusedLoadId
      ) {
        return undefined;
      }

      if (
        Number(
          focusedLoadHandledRef.current
        ) ===
        Number(
          focusedLoadId
        )
      ) {
        return undefined;
      }

      const frameId =
        window.requestAnimationFrame(
          () => {
            const container =
              scrollRef.current;

            if (
              !container
            ) {
              return;
            }

            const targetRow =
              container.querySelector(
                `[data-load-id="${focusedLoadId}"]`
              );

            if (
              !targetRow
            ) {
              return;
            }

            targetRow.scrollIntoView({
              behavior:
                "smooth",

              block:
                "center",

              inline:
                "nearest",
            });

            focusedLoadHandledRef.current =
              focusedLoadId;
          }
        );

      return () => {
        window.cancelAnimationFrame(
          frameId
        );
      };
    },
    [
      focusedLoadId,
      selectedView,
      visibleCurrentEntries,
    ]
  );

  // ✅ Preload lightweight points for visible sessions so chips show real names/plates
  // immediately, without needing to expand the TransportSession first.
  const visibleCurrentSessionIds = useMemo(() => {
    if (selectedView !== "current") return [];

    return (visibleCurrentEntries || [])
      .map((entry) => entry?.transportsessionId)
      .filter(Boolean)
      .map(String);
  }, [selectedView, visibleCurrentEntries]);

  useEffect(() => {
    if (selectedView !== "current") return;
    if (!visibleCurrentSessionIds.length) return;

    primeAndFetchSessionPoints(
      visibleCurrentSessionIds,
      axios,
      setSessionPoints,
      sessionPointsCacheRef,
      inflightSessionsRef,
      (sid, rows) => {
        setSessionProgress((prev) => ({
          ...prev,
          [String(sid)]: computeSessionProgressFromPoints(rows || []),
        }));

        setProgressAnimated((prev) => ({
          ...prev,
          [String(sid)]: true,
        }));
      }
    );
  }, [selectedView, visibleCurrentSessionIds.join(",")]);

  // ✅ helps IntersectionObserver not keep increasing forever
  const totalVisibleLoadCountAvailable = useMemo(() => {
    return (currentSessionEntries || []).reduce((acc, e) => {
      const loadsCount = Array.isArray(e.filteredLoads) ? e.filteredLoads.length : 0;
      return acc + loadsCount;
    }, 0);
  }, [currentSessionEntries]);

  const visibleLoadCountNow = useMemo(() => {
    return (visibleCurrentEntries || []).reduce((acc, e) => {
      const loadsCount = Array.isArray(e.filteredLoads) ? e.filteredLoads.length : 0;
      return acc + loadsCount;
    }, 0);
  }, [visibleCurrentEntries]);

  const hasMoreCurrent = useMemo(() => {
      return selectedView === "current" && visibleLoadCountNow < totalVisibleLoadCountAvailable;
    }, [selectedView, visibleLoadCountNow, totalVisibleLoadCountAvailable]);

    useEffect(() => {
    if (selectedView !== "current") return;
    if (!hasMoreCurrent) return;

    const el = scrollRef.current;
    if (!el) return;

    const lacksOverflow = el.scrollHeight <= el.clientHeight + 4;

    if (lacksOverflow) {
      setVisibleRowBudget((prev) => {
        const next = prev + LOADS_PAGE_SIZE;
        return Math.min(next, totalVisibleLoadCountAvailable || next);
      });
    }
  }, [
    selectedView,
    hasMoreCurrent,
    visibleCurrentEntries,
    totalVisibleLoadCountAvailable,
  ]);

  useLayoutEffect(() => {
    const timers = [];

    Object.keys(openingSessions).forEach((sid) => {
      if (!openingSessions[sid]) return;
      if (!expandedSessions[sid]) return;
      if (closingSessions[sid]) return;

      const wrapper = expandedSectionRefs.current[sid];
      const inner = expandedInnerRefs.current[sid];

      if (!wrapper || !inner) return;

      wrapper.style.height = "0px";
      wrapper.style.opacity = "0";
      wrapper.style.transform = "translateY(-10px)";
      wrapper.style.overflow = "hidden";

      // força o browser a registar o estado inicial antes de animar
      void wrapper.offsetHeight;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const targetHeight = inner.scrollHeight;

          wrapper.style.height = `${targetHeight}px`;
          wrapper.style.opacity = "1";
          wrapper.style.transform = "translateY(0)";
        });
      });

      const timer = window.setTimeout(() => {
        const stillOpen = expandedSessions[sid] && !closingSessions[sid];
        const sameWrapper = expandedSectionRefs.current[sid];

        if (stillOpen && sameWrapper) {
          sameWrapper.style.height = "auto";
          sameWrapper.style.opacity = "1";
          sameWrapper.style.transform = "translateY(0)";
          sameWrapper.style.overflow = "visible";
        }
      }, SESSION_SLIDE_MS + 40);

      timers.push(timer);
    });

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [openingSessions, expandedSessions, closingSessions]);

  useEffect(() => {
    const observers = [];

    Object.keys(expandedSessions).forEach((sid) => {
      if (!expandedSessions[sid]) return;
      if (closingSessions[sid]) return;

      const wrapper = expandedSectionRefs.current[sid];
      const inner = expandedInnerRefs.current[sid];

      if (!wrapper || !inner) return;

      const syncHeight = () => {
        if (!expandedSessions[sid]) return;
        if (closingSessions[sid]) return;

        // se já acabou a abertura e está em auto, não mexemos
        if (wrapper.style.height === "auto" && !openingSessions[sid]) return;

        wrapper.style.height = `${inner.scrollHeight}px`;
      };

      const observer = new ResizeObserver(() => {
        requestAnimationFrame(syncHeight);
      });

      observer.observe(inner);
      observers.push(observer);
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, [openingSessions, expandedSessions, closingSessions]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const v = params.get("view");
    if (["pendent", "current", "history"].includes(v)) {
      setSelectedView(v);
    } else {
      setSelectedView("current");
      navigate(`/loads?view=current`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleToggle = (v) => {
    if (v !== selectedView) navigate(`/loads?view=${v}`);
  };

  const handleLoadsScroll = useCallback(() => {
    if (selectedView !== "current") return;
    if (!hasMoreCurrent) return;

    const el = scrollRef.current;
    if (!el) return;

    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 120;

    if (!nearBottom) return;
    if (loadingMoreRef.current) return;

    loadingMoreRef.current = true;
    setIsLoadingMore(true);

    setVisibleRowBudget((prev) => {
      const next = prev + LOADS_PAGE_SIZE;
      return Math.min(next, totalVisibleLoadCountAvailable || next);
    });
  }, [selectedView, hasMoreCurrent, totalVisibleLoadCountAvailable]);

  // ✅ Auto-finalize for external/subcontracted loads (no inventory, no modal)
  // Note: do NOT reference scheduleRefreshLoads here (TDZ). Use refreshLoads instead.
  const finalizeExternalLoadNoInventory = async (loadId, transportsessionId) => {
    const lid = Number(loadId);
    if (!lid) return;

    if (externalFinalizeInFlightRef.current.has(lid)) return;
    externalFinalizeInFlightRef.current.add(lid);

    try {
      console.log("[Loads] auto-finalize external load (no inventory)", {
        loadId: lid,
        transportsessionId,
      });

      await axios.post(`/loads/${lid}/finalize-cmr`);

      showSuccessToast(t("loads.toast.completedNowInHistory"));
      // ✅ refresh list immediately so it moves to History
      await refreshLoads();
    } catch (e) {
      console.error("[Loads] auto-finalize external failed", e);
      externalFinalizeInFlightRef.current.delete(lid);
    }
  };

  const buildCmrPointContext = useCallback((point) => {
    if (!point?.id) return null;

    const type = String(point.type || "").toUpperCase();

    const pointLabel =
      type === "PICKUP"
        ? `Recolha #${point.sequence ?? ""}`.trim()
        : type === "DELIVERY"
        ? `Entrega #${point.sequence ?? ""}`.trim()
        : `Ponto #${point.sequence ?? ""}`.trim();

    return {
      pickupDeliveryId: point.id,
      pointLabel,
      pointLocation: point.location || point.city || point.country || null,
    };
  }, []);

  const getLastDeliveryPointContextForLoad = useCallback(
    (loadId, points = []) => {
      const deliveries = (points || []).filter(
        (p) =>
          Number(p.loadId) === Number(loadId) &&
          String(p.type || "").toUpperCase() === "DELIVERY"
      );

      if (!deliveries.length) return null;

      const sorted = deliveries.slice().sort((a, b) => {
        const key = (x) => {
          if (x.seq != null) return Number(x.seq);
          if (x.sequence != null) return Number(x.sequence);

          const hhmm = String(x.hour || "00:00").padStart(5, "0");
          return new Date(`${x.date || "1970-01-01"}T${hhmm}:00`).getTime();
        };

        return key(a) - key(b);
      });

      return buildCmrPointContext(sorted[sorted.length - 1]);
    },
    [buildCmrPointContext]
  );

  const openCmrModalAndFetch = useCallback(async (loadId, pointContext = null) => {
    console.log("[Loads] openCmrModalAndFetch start", {
      loadId,
      pointContext,
    });

    setSelectedLoadForCmr(loadId);

    setCmrUploadContext({
      loadId,
      pickupDeliveryId: pointContext?.pickupDeliveryId ?? null,
      pointLabel: pointContext?.pointLabel ?? null,
      pointLocation: pointContext?.pointLocation ?? null,
    });

    try {
      const { data } = await axios.get(`/loads/loadDocPhotos/${loadId}`, {
        params: { t: Date.now() },
      });

      console.log("[Loads] /loadDocPhotos response", data);

      const arr = Array.isArray(data?.loadDocPhotos) ? data.loadDocPhotos : [];

      console.log("[Loads] normalized existingCmrUrls", arr);

      setExistingCmrUrls(arr);
    } catch (e) {
      console.error("[Loads] /loadDocPhotos error", e);
      setExistingCmrUrls([]);
    } finally {
      console.log("[Loads] opening modal");
      setCmrModalOpen(true);
    }
  }, []);

  // --- Fetch Loads (resumo+progresso) ---
  const refreshLoads = useCallback(async (reason = "manual") => {
    const now = Date.now();
  
    if (refreshLoadsInFlightRef.current) {
      console.log("[Loads] refreshLoads skipped — already in flight", { reason });
      return;
    }
  
    if (now - lastRefreshLoadsAtRef.current < 2000) {
      console.log("[Loads] refreshLoads skipped — too soon", { reason });
      return;
    }
  
    refreshLoadsInFlightRef.current = true;
    lastRefreshLoadsAtRef.current = now;
  
    try {
      console.time(`[Loads] refreshLoads ${reason}`);
  
      const { data } = await axios.get("/loads/summary-with-progress", {
        params: { withPoints: 0, limit: 5000 },
      });
  
      console.timeEnd(`[Loads] refreshLoads ${reason}`);

      console.log("[Loads] summary-with-progress counts", {
        loads: Array.isArray(data?.loads) ? data.loads.length : null,
        emptySessions: Array.isArray(data?.emptySessions) ? data.emptySessions.length : null,
      });

      const emptySessions = Array.isArray(data?.emptySessions) ? data.emptySessions : [];

      // normalize rows for the table
      const loadsList = (data.loads || []).map((load) => ({
        ...load,

        // ✅ guarantee carrierId is present for downstream "external carrier" checks
        carrierId:
          load.carrierId ??
          load.carrier_id ??
          load.carrierID ??
          load.carrier?.id ??
          load.loadsAsCarrier?.id ??
          null,

        shipmentRequestId:
          load.shipmentRequestId ??
          load.shipment_request_id ??
          null,

        uiCounter: load.loadNumber ?? load.id,
        tsUiCounter: load.transportSession?.transportSessionNumber ?? null,
        pickupLocation: load.pickupLocation || t("loads.common.na"),
        deliveryLocation: load.deliveryLocation || t("loads.common.na"),
        pickupDeliveries: [],

        // 🚚 Truck (summary fallback)
        pickupTruck: load.truckPickupInfo ? { plate: String(load.truckPickupInfo).split("•")[0].trim() } : null,
        deliveryTruck: load.truckDeliveryInfo ? { plate: String(load.truckDeliveryInfo).split("•")[0].trim() } : null,

        // 🛻 Trailer (summary fallback)
        pickupTrailer: load.trailerPickupInfo ? { plate: String(load.trailerPickupInfo).split("•")[0].trim() } : null,
        deliveryTrailer: load.trailerDeliveryInfo ? { plate: String(load.trailerDeliveryInfo).split("•")[0].trim() } : null,

        // 🧑‍✈️ Driver (summary fallback)
        pickupDriver: load.driverPickupInfo ? { name: String(load.driverPickupInfo).split("•")[0].trim() } : null,
        deliveryDriver: load.driverDeliveryInfo ? { name: String(load.driverDeliveryInfo).split("•")[0].trim() } : null,

        status: String(load.status || "").toLowerCase(),
        price: load.price ?? load.totalPrice ?? null,
      }));

      // group by session
      const grouped = loadsList.reduce((acc, load) => {
        const sid = String(load.transportsessionId);
        if (!acc[sid]) {
          acc[sid] = {
            loads: [],
            totalLength: 0,
            loadBearers: new Set(),
            tsUiCounter: load.tsUiCounter ?? null,
            isStrategicOnly: false,
          };
        } else if (!acc[sid].tsUiCounter && load.tsUiCounter) {
          acc[sid].tsUiCounter = load.tsUiCounter;
        }
        const loadLength = parseFloat(load.length) || 0;
        acc[sid].loads.push(load);
        if (!load.superImposable) acc[sid].totalLength += loadLength;
        if (load.loadBearer) acc[sid].loadBearers.add(load.id);
        return acc;
      }, {});

      // ✅ Add sessions with OPEN strategic points but ZERO loads in current list
      (emptySessions || []).forEach((s) => {
        const sid = String(s.transportsessionId);
        if (!grouped[sid]) {
          grouped[sid] = {
            loads: [],
            totalLength: 0,
            loadBearers: new Set(),
            tsUiCounter: s.transportSessionNumber ?? null,
            isStrategicOnly: true,
          };
        }
      });

      // orphaned superimposables contribute to space
      Object.values(grouped).forEach((session) => {
        const orphaned = session.loads.filter(
          (l) => l.superImposable && !session.loadBearers.has(l.associatedLoadBearerId)
        );
        const orphanedLength = orphaned.reduce((a, l) => a + parseFloat(l.length || 0), 0);
        session.totalLength = parseFloat((session.totalLength + orphanedLength).toFixed(2));
      });

      setSessionProgress(data.sessionProgress || {});

      // Keep strategic-only sessions alive using either backend signal OR our short TTL cache
      const activeSet = new Set((data.activeStrategicSessions || []).map(String));

      for (const [sid, entry] of Object.entries(sessionPointsCacheRef.current || {})) {
        const rows = entry?.rows;
        if (Array.isArray(rows) && hasPendingStrategic(rows)) {
          activeSet.add(String(sid));
        }
      }

      setActiveStrategicSessions(activeSet);

      setLoads(loadsList);
      setGroupedLoads(grouped);

      // ✅ Persist cache (para abrir instantâneo na próxima visita)
      try {
        writeLoadsCache(loadsCurrentCacheKey, {
          loads: loadsList,
          groupedLoads: serializeGroupedLoads(grouped),
          sessionProgress: data.sessionProgress || {},

          // ✅ Não persistir assignments por ponto na cache longa das Loads.
          // Estes dados vêm de /pickupdeliveries/by-sessions ou da expanded section.
          // Persistir isto causava chips antigos nos botões "All".
          sessionPoints: {},

          activeStrategicSessions: Array.from(activeSet || []),
        });
      } catch {
        // ignore
      }

      // ✅ Não carregar pontos de todas as sessões no arranque.
      // A tabela principal deve abrir só com os dados denormalizados da Load.
      // Os pontos são carregados apenas quando a sessão é expandida ou quando um chip precisa deles.
      if (data.sessionPoints && Object.keys(data.sessionPoints).length) {
        setSessionPoints((prev) => ({ ...prev, ...data.sessionPoints }));

        for (const [sid, rows] of Object.entries(data.sessionPoints)) {
          sessionPointsCacheRef.current[sid] = { ts: Date.now(), rows };
        }
      }

      setProgressAnimated((prev) => {
        const next = { ...prev };
        Object.keys(grouped).forEach((id) => {
          next[id] = true;
        });
        return next;
      });

      setBaseTableRendered(true);
      setErrorMessage("");
    } catch (error) {
      console.error("[Loads] refreshLoads failed", { reason, error });
      setErrorMessage(t("loads.errors.loadFailed"));
    } finally {
      refreshLoadsInFlightRef.current = false;
    }
    }, [t, loadsCurrentCacheKey]);

  // ✅ Debounced refresh (coalesce many WS events into 1 refresh)
  const refreshRafRef = useRef(0);
  const refreshPendingRef = useRef(false);

  const scheduleRefreshLoads = useCallback(() => {
    if (refreshPendingRef.current) return;
    refreshPendingRef.current = true;

    if (refreshRafRef.current) cancelAnimationFrame(refreshRafRef.current);
    refreshRafRef.current = requestAnimationFrame(() => {
      refreshPendingRef.current = false;
      refreshLoads();
    });
  }, [refreshLoads]);

  // Pendent loads (summary, join-free)
  const fetchPendentLoads = useCallback(async () => {
    try {
      const { data } = await axios.get("/pendentloads/summary");
      const arr = data?.loads || [];
      setPendentLoads(arr);
      setErrorMessage("");

      // ✅ cache pendent summary
      writeLoadsCache(loadsPendentCacheKey, { loads: Array.isArray(arr) ? arr : [] });
    } catch (error) {
      console.error("❌ Failed to fetch pendent loads", error);
      setErrorMessage(t("loads.errors.pendentFailed"));
    }
  }, [t]);

  // Refresh por view
  useEffect(() => {
    if (selectedView === "current") {
      refreshLoads("view-current");
    }
    if (selectedView === "pendent") {
      fetchPendentLoads();
    }
  }, [selectedView, refreshLoads, fetchPendentLoads]);

  // Limpa pesquisa quando muda de view
  useEffect(() => {
    setSearchTerm("");
    // reset paging when switching tabs
    setVisibleRowBudget(LOADS_PAGE_SIZE);
  }, [selectedView]);

  useEffect(() => {
    // reset paging when searching (so results start from the top)
    if (selectedView === "current") setVisibleRowBudget(LOADS_PAGE_SIZE);
  }, [searchTerm, selectedView]);

  useEffect(() => {
    // ✅ se já não há mais, garante que não fica preso em "A carregar..."
    if (selectedView !== "current") {
      setIsLoadingMore(false);
      loadingMoreRef.current = false;
      return;
    }

    if (!hasMoreCurrent) {
      setIsLoadingMore(false);
      loadingMoreRef.current = false;
      return;
    }

    // ✅ quando o budget aumenta, terminamos o "loading"
    if (visibleRowBudget > lastBudgetRef.current) {
      lastBudgetRef.current = visibleRowBudget;
      setIsLoadingMore(false);
      loadingMoreRef.current = false;
    } else {
      lastBudgetRef.current = visibleRowBudget;
    }
  }, [selectedView, hasMoreCurrent, visibleRowBudget]);

  // 🔌 Live updates for Loads (mirrors Places)
  useEffect(() => {
    // Only live-subscribe while on "current" view (you can remove this guard if you want all views)
    if (selectedView !== "current") return;

    const s = getSocket();

    const subscribe = () => {
      if (s.connected) {
        s.emit("subscribe:loads");
      }
    };

    s.on("connect", subscribe);
    s.on("reconnect", subscribe);

    if (s.connected) {
      subscribe();
    }

    const onCreated = () => {
      if (shouldIgnoreEarlySocketRefresh()) return;
      scheduleRefreshLoads();
    };

    const onUpdated = (payload) => {
      applyLoadPatchInstant(payload);
    
      if (shouldIgnoreEarlySocketRefresh()) {
        return;
      }
    
      if (payload?.sessionPoints && payload?.sessionId) {
        const sid = String(payload.sessionId);

        setSessionPoints((prev) => ({
          ...prev,
          [sid]: payload.sessionPoints,
        }));

        sessionPointsCacheRef.current[sid] = {
          ts: Date.now(),
          rows: payload.sessionPoints,
        };
      }

      if (payload?.sessionProgressDelta && payload?.sessionId) {
        setSessionProgress((prev) => ({
          ...prev,
          [payload.sessionId]: payload.sessionProgressDelta,
        }));
      }

      // ✅ 2) Continua a fazer refresh em background para garantir consistência
      scheduleRefreshLoads();
    };

    const onDeleted = ({ id, sessionId }) => {
      setLoads((prev) => prev.filter((l) => l.id !== id));
    
      setGroupedLoads((prev) => {
        const next = { ...prev };
        for (const sid of Object.keys(next)) {
          next[sid] = {
            ...next[sid],
            loads: next[sid].loads.filter((l) => l.id !== id),
          };
        }
        return next;
      });
    
      if (shouldIgnoreEarlySocketRefresh()) return;
    
      refreshLoads("socket-delete");
    };

    const onSessionPoints = ({ sessionId, points }) => {
      const sid = String(sessionId);
      const rows = points || [];

      setSessionPoints((prev) => ({ ...prev, [sid]: rows }));

      sessionPointsCacheRef.current[sid] = {
        ts: Date.now(),
        rows,
      };

      setSessionProgress((prev) => ({
        ...prev,
        [sid]: computeSessionProgressFromPoints(rows),
      }));

      setProgressAnimated((prev) => ({
        ...prev,
        [sid]: true,
      }));
    };

    s.on("load:created", onCreated);
    s.on("load:updated", onUpdated);
    s.on("load:deleted", onDeleted);
    s.on("session:points", onSessionPoints);

    // ✅ Updates diretos de pickup/delivery:
    // truck/trailer/driver single assignment, strategic points, etc.
    s.on("pd:updated", applyPickupDeliveryPatchInstant);

    const onSessionProgress = ({ sessionId, pct }) => {
      setSessionProgress((prev) => ({ ...prev, [sessionId]: pct | 0 }));
      setProgressAnimated((prev) => ({ ...prev, [sessionId]: true }));
    };
    s.on("session:progress", onSessionProgress);

    const onSessionPointsStale = ({ sessionId }) => {
      const sid = String(sessionId);

      delete sessionPointsCacheRef.current[sessionId];
      delete sessionPointsCacheRef.current[sid];

      setSessionPoints((prev) => {
        const next = { ...prev };
        delete next[sessionId];
        delete next[sid];
        return next;
      });

      primeAndFetchSessionPoints(
        [sid],
        axios,
        setSessionPoints,
        sessionPointsCacheRef,
        inflightSessionsRef,
        (sid2, rows) => {
          setSessionProgress((prev) => ({
            ...prev,
            [String(sid2)]: computeSessionProgressFromPoints(rows || []),
          }));

          setProgressAnimated((prev) => ({
            ...prev,
            [String(sid2)]: true,
          }));
        }
      );
    };
    s.on("session:points:stale", onSessionPointsStale);

    const onPdStatus = ({ transportsessionId }) => {
      const sid = String(transportsessionId);

      delete sessionPointsCacheRef.current[sid];

      // fetch points + quando chegarem, recalcula progress
      primeAndFetchSessionPoints(
        [sid],
        axios,
        setSessionPoints,
        sessionPointsCacheRef,
        inflightSessionsRef,
        (sid2, rows) => {
          setSessionProgress((prev) => ({
            ...prev,
            [String(sid2)]: computeSessionProgressFromPoints(rows || []),
          }));
          setProgressAnimated((prev) => ({ ...prev, [String(sid2)]: true }));
        }
      );
    };
    s.on("pd:status", onPdStatus);

    return () => {
      s.off("connect", subscribe);
      s.off("reconnect", subscribe);
      s.off("load:created", onCreated);
      s.off("load:updated", onUpdated);
      s.off("load:deleted", onDeleted);
      s.off("session:points", onSessionPoints);
      s.off("pd:updated", applyPickupDeliveryPatchInstant);
      s.off("session:progress", onSessionProgress);
      s.off("session:points:stale", onSessionPointsStale);
      s.off("pd:status", onPdStatus);
    };
  }, [
    selectedView,
    refreshLoads,
    scheduleRefreshLoads,
    applyLoadPatchInstant,
    applyPickupDeliveryPatchInstant,
  ]);

  const sessionIdsKey = useMemo(() => {
    if (selectedView !== "current") return "";
    return Object.keys(groupedLoads || {}).sort().join(",");
  }, [selectedView, groupedLoads]);

  // join all listed sessions even when collapsed
  useEffect(() => {
    if (selectedView !== "current") return;
    if (!sessionIdsKey) return;

    const s = getSocket();
    const ids = sessionIdsKey.split(",").filter(Boolean);

    if (!ids.length) return;

    if (s.connected) {
      s.emit("subscribe:sessions", { ids });
    }

    const onConnect = () => {
      s.emit("subscribe:sessions", { ids });
    };

    s.on("connect", onConnect);
    s.on("reconnect", onConnect);

    return () => {
      s.off("connect", onConnect);
      s.off("reconnect", onConnect);

      if (s.connected) {
        s.emit("unsubscribe:sessions", { ids });
      }
    };
  }, [selectedView, sessionIdsKey]);

  // Organiza loads por view (para contagens/empty states)
  const organizedLoads = useMemo(
    () => ({
      pendent: pendentLoads,
      current: loads.filter((load) => load.status === "current"),
      history: loads.filter((load) => load.status === "completed" || load.status === "cancelled"),
    }),
    [pendentLoads, loads]
  );

  // Lista “visível” para contagem rápida (não altera a tabela current que é grouped)
  const displayedLoads = useMemo(
    () => filterLoads(organizedLoads[selectedView] || loads, searchTerm),
    [organizedLoads, selectedView, loads, searchTerm]
  );

  // Contadores (pills no header)
  const totalCount = organizedLoads[selectedView]?.length || 0;

  const visibleCountCurrent = useMemo(() => {
    return (visibleCurrentEntries || []).reduce((acc, e) => {
      const n = Array.isArray(e?.filteredLoads) ? e.filteredLoads.length : 0;
      return acc + n;
    }, 0);
  }, [visibleCurrentEntries]);

  const [historyVisibleCount, setHistoryVisibleCount] = useState(null);

  const visibleCount =
    selectedView === "current"
      ? visibleCountCurrent
      : selectedView === "history"
      ? (historyVisibleCount ?? displayedLoads.length)
      : displayedLoads.length;
  
  const hasLoadsSearch = String(searchTerm || "").trim().length > 0;
  
  const hasCurrentLoads = (organizedLoads.current || []).length > 0;
  
  const hasCurrentVisibleResults = (currentSessionEntries || []).length > 0;
  
  // Recompute progress for ONE session (usado pelo filho)
  const refreshSessionStrategics = useCallback(
    (sid, pointsFromChild) => {
  const sidStr = String(sid);

  // 1) Prefer points from child; if not provided, reuse cached sessionPoints; only then try groupedLoads
  const cachedPoints = Array.isArray(sessionPoints?.[sidStr]) ? sessionPoints[sidStr] : null;
  const groupedPoints = (groupedLoads?.[sidStr]?.loads || []).flatMap((l) => l.pickupDeliveries || []);
  const allPoints = pointsFromChild ?? cachedPoints ?? groupedPoints ?? [];

  // 2) If we have points, update progress; if not, keep whatever was already there
  if (Array.isArray(allPoints) && allPoints.length > 0) {
    const prog = computeSessionProgressFromPoints(allPoints);
    setSessionProgress((prev) => ({ ...prev, [sidStr]: prog }));
  }

  // 3) Critical: don’t delete the session from activeStrategicSessions just because we had no points in this call
  const hadOpenStrategicBefore = activeStrategicSessions.has(sidStr);
  const hasOpenStrategicNow =
    Array.isArray(allPoints) && allPoints.length > 0 ? hasPendingStrategic(allPoints) : hadOpenStrategicBefore;

  setActiveStrategicSessions((prev) => {
    const next = new Set(prev);
    if (hasOpenStrategicNow) next.add(sidStr);
    else next.delete(sidStr);
    return next;
  });

  // 4) Se o filho enviou pontos, isso é a fonte mais recente.
  // Mesmo que venha [], devemos substituir para não manter dados antigos.
  if (Array.isArray(pointsFromChild)) {
    sessionPointsCacheRef.current[sidStr] = {
      ts: Date.now(),
      rows: pointsFromChild,
    };

    setSessionPoints((prev) => ({
      ...prev,
      [sidStr]: pointsFromChild,
    }));
  } else if (Array.isArray(allPoints) && allPoints.length > 0) {
    sessionPointsCacheRef.current[sidStr] = {
      ts: Date.now(),
      rows: allPoints,
    };

    setSessionPoints((prev) => ({
      ...prev,
      [sidStr]: allPoints,
    }));
  }

  setProgressAnimated((prev) => ({ ...prev, [sidStr]: true }));

    const sessionClosed =
      Array.isArray(allPoints) &&
      allPoints.length > 0 &&
      allPoints.every((p) => ["COMPLETED", "CANCELLED"].includes(normalizeStatus(p?.status)));

    const lastPointIsStrategic = (() => {
      const sorted = (allPoints || []).slice().sort((a, b) => {
        const key = (x) => {
          if (x.seq != null) return Number(x.seq);
          const hhmm = (x.hour || "00:00").padStart(5, "0");
          return new Date(`${x.date || "1970-01-01"}T${hhmm}:00`).getTime();
        };
        return key(a) - key(b);
      });
      const last = sorted[sorted.length - 1];
      return String(last?.type || "").toUpperCase() === "STRATEGICPOINT";
    })();

    const loadHasTerminalLastDelivery = (loadId) => {
      const deliveries = (allPoints || []).filter(
        (p) => Number(p.loadId) === Number(loadId) && String(p.type || "").toUpperCase() === "DELIVERY"
      );
      if (!deliveries.length) return false;

      const sorted = deliveries.slice().sort((a, b) => {
        const key = (x) => {
          if (x.seq != null) return Number(x.seq);
          const hhmm = (x.hour || "00:00").padStart(5, "0");
          return new Date(`${x.date || "1970-01-01"}T${hhmm}:00`).getTime();
        };
        return key(a) - key(b);
      });

      const lastDelivery = sorted[sorted.length - 1];
      return ["COMPLETED", "CANCELLED"].includes(normalizeStatus(lastDelivery?.status));
    };

    if (selectedView === "current" && sessionClosed && !cmrModalOpen) {
      const loadsInSession = groupedLoads?.[String(sid)]?.loads || [];

      const candidate = loadsInSession.find(
        (l) =>
          String(l.status || "").toLowerCase() === "current" &&
          loadHasTerminalLastDelivery(l.id) &&
          !pendingCmrUploads.has(l.id)
      );

      if (candidate?.id) {
        // ✅ External/subcontracted: finalize immediately (no inventory, no CMR modal)
        if (isExternalTransportCarrier(candidate)) {
          finalizeExternalLoadNoInventory(candidate.id, sid);
          return;
        }

        // ✅ Internal: only open CMR flow when the session ends on a strategic point (your existing behavior)
        if (lastPointIsStrategic) {
          const pointContext = getLastDeliveryPointContextForLoad(candidate.id, allPoints);

          openCmrModalAndFetch(candidate.id, pointContext);
        }
      }
    }
  },
    [
      groupedLoads,
      selectedView,
      cmrModalOpen,
      pendingCmrUploads,
      openCmrModalAndFetch,
      sessionPoints,
      activeStrategicSessions,
      getLastDeliveryPointContextForLoad,
    ]
  );

  // Ações
  const handleRemove = async (id) => {
    const confirmDelete = window.confirm(t("loads.confirm.removeLoad"));
    if (!confirmDelete) return;
    try {
      const currentScrollTop = scrollRef.current?.scrollTop || 0;
      const loadToDelete = loads.find((load) => load.id === id);
      if (loadToDelete?.loadBearer) {
        const orphanedLoads = loads.filter((load) => load.associatedLoadBearerId === id);
        for (let orphan of orphanedLoads) {
          await axios.put(`/loads/${orphan.id}`, {
            associatedLoadBearerId: null,
            superImposable: false,
          });
        }
      }

      // Optimistic UI
      setLoads((prev) => prev.filter((l) => l.id !== id));
      setGroupedLoads((prev) => {
        const next = { ...prev };
        for (const sid of Object.keys(next)) {
          next[sid] = { ...next[sid], loads: next[sid].loads.filter((l) => l.id !== id) };
        }
        return next;
      });

      await axios.delete(`/loads/${id}`);
      await refreshLoads("after-mutation");

      setReloadTransportSessionKey((prev) => prev + 1);
      setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = currentScrollTop;
        }
      }, 0);
    } catch (error) {
      console.error("Error deleting load:", error);
      setErrorMessage(t("loads.errors.deleteFailed"));
    }
  };

  const handleEditLoad = async (id) => {
    const row =
      loads.find(
        (load) =>
          load.id === id
      ) ||
      {};

    const editPath =
      `/loads/${id}/edit`;

    try {
      const {
        data,
      } =
        await axios.get(
          `/loads/${id}/editloads`
        );

      const prefill =
        normalizeLoadForPrefill(
          row,
          data
        );

      navigate(
        editPath,
        {
          state: {
            prefill,
          },
        }
      );
    } catch (
      error
    ) {
      console.error(
        "Prefetch for edit failed:",
        error
      );

      navigate(
        editPath
      );
    }
  };

  const handleAddLoad = (transportsessionId, remainingLength) => {
    navigate(`/loads/create?transportsessionId=${transportsessionId}&maxLength=${remainingLength.toFixed(2)}`);
  };

  const handleAddSuperImposableLoad = (transportsessionId, loadsInSession) => {
    const availableLoadBearer = loadsInSession.find((load) => {
      if (!load.loadBearer) return false;
      const bearerLen = parseFloat(load.length || 0);
      if (isNaN(bearerLen)) return false;
      const totalSuperimposed = loadsInSession
        .filter((l) => l.associatedLoadBearerId === load.id)
        .reduce((acc, l) => acc + parseFloat(l.length || 0), 0);
      return totalSuperimposed < bearerLen;
    });

    if (!availableLoadBearer) {
      alert(t("loads.alerts.noSpaceSuperimposable"));
      return;
    }

    const superimposableLoads = loadsInSession.filter((load) => load.associatedLoadBearerId === availableLoadBearer.id);
    const totalSuperimposableLength = superimposableLoads.reduce((acc, load) => acc + parseFloat(load.length || 0), 0);

    const remainingSpace = Math.max(0, parseFloat(availableLoadBearer.length || 0) - totalSuperimposableLength);
    if (remainingSpace <= 0) {
      alert(t("loads.alerts.noSpaceSuperimposable"));
      return;
    }

    navigate(
      `/loads/superimposable-create?transportsessionId=${transportsessionId}&maxLength=${remainingSpace}&loadBearerId=${availableLoadBearer.id}`
    );
  };

  const ensureSessionPointsLoaded = useCallback(
    async (transportsessionId) => {
      const sid = String(transportsessionId);
  
      const cached = sessionPointsCacheRef.current[sid];
  
      if (
        cached &&
        Date.now() - cached.ts < SESSION_POINTS_TTL_MS &&
        Array.isArray(cached.rows)
      ) {
        setSessionPoints((prev) => ({
          ...prev,
          [sid]: cached.rows,
        }));
        return;
      }
  
      try {
        setLoadingSessions((prev) => ({
          ...prev,
          [sid]: true,
        }));
  
        const { data } = await axios.get(
          `/pickupdeliveries/${sid}/light?sort=seq&t=${Date.now()}`
        );
  
        const rows = Array.isArray(data) ? data : [];
  
        sessionPointsCacheRef.current[sid] = {
          ts: Date.now(),
          rows,
        };
  
        setSessionPoints((prev) => ({
          ...prev,
          [sid]: rows,
        }));
  
        setSessionProgress((prev) => ({
          ...prev,
          [sid]: computeSessionProgressFromPoints(rows),
        }));
  
        setActiveStrategicSessions((prev) => {
          const next = new Set(prev);
          if (hasPendingStrategic(rows)) next.add(sid);
          else next.delete(sid);
          return next;
        });
      } catch (err) {
        console.error("[Loads] Failed to fetch session points", {
          transportsessionId: sid,
          err,
        });
      } finally {
        setLoadingSessions((prev) => ({
          ...prev,
          [sid]: false,
        }));
      }
    },
    []
  );
  
  const toggleExpandSession = (transportsessionId) => {
    const sid = String(transportsessionId);
    const isCurrentlyOpen = !!expandedSessions[sid] && !closingSessions[sid];

    if (closingSessions[sid]) return;

    // ABRIR
    if (!isCurrentlyOpen) {
      ensureSessionPointsLoaded(sid);

      setClosingSessions((prev) => {
        const next = { ...prev };
        delete next[sid];
        return next;
      });

      setOpeningSessions((prev) => ({
        ...prev,
        [sid]: true,
      }));

      setExpandedSessions((prev) => ({
        ...prev,
        [sid]: true,
      }));

      window.setTimeout(() => {
        setOpeningSessions((prev) => {
          const next = { ...prev };
          delete next[sid];
          return next;
        });
      }, SESSION_SLIDE_MS);

      return;
    }

    // FECHAR
    const wrapper = expandedSectionRefs.current[sid];
    const inner = expandedInnerRefs.current[sid];

    if (wrapper) {
      const currentHeight = inner?.scrollHeight || wrapper.scrollHeight || wrapper.offsetHeight;

      wrapper.style.height = `${currentHeight}px`;
      wrapper.style.opacity = "1";
      wrapper.style.transform = "translateY(0)";
      wrapper.style.overflow = "hidden";

      // força o browser a aceitar a altura atual antes de ir para 0
      void wrapper.offsetHeight;
    }

    setOpeningSessions((prev) => {
      const next = { ...prev };
      delete next[sid];
      return next;
    });

    setClosingSessions((prev) => ({
      ...prev,
      [sid]: true,
    }));

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const closingWrapper = expandedSectionRefs.current[sid];

        if (closingWrapper) {
          closingWrapper.style.height = "0px";
          closingWrapper.style.opacity = "0";
          closingWrapper.style.transform = "translateY(-10px)";
          closingWrapper.style.overflow = "hidden";
        }

        setExpandedSessions((prev) => ({
          ...prev,
          [sid]: false,
        }));
      });
    });

    window.setTimeout(() => {
      setClosingSessions((prev) => {
        const next = { ...prev };
        delete next[sid];
        return next;
      });
    }, SESSION_SLIDE_MS);
  };

  // Bulk assign (pickup)
  const handleBulkTruckAssign = async (loadId, truckId, transportsessionId, truckPlate) => {
    try {
      await axios.put("/pickupdeliveries/assign-truck-to-all-pickups", { loadId, truckId });
      expandedRefs.current[transportsessionId]?.updatePickupTrucksForLoad?.(loadId, { id: truckId, plate: truckPlate });

      setLoads((prev) =>
        prev.map((l) => {
          if (l.id !== loadId) return l;
          const nextPDs = (l.pickupDeliveries || []).map((pd) =>
            pd.type === "PICKUP" ? { ...pd, truckId, truck: { ...(pd.truck || {}), id: truckId, plate: truckPlate } } : pd
          );
          return { ...l, pickupTruck: { plate: truckPlate }, pickupDeliveries: nextPDs };
        })
      );

      setGroupedLoads((prev) => {
        const session = prev?.[transportsessionId];
        if (!session) return prev;
        const nextLoads = session.loads.map((l) => {
          if (l.id !== loadId) return l;
          const nextPDs = (l.pickupDeliveries || []).map((pd) =>
            pd.type === "PICKUP" ? { ...pd, truckId, truck: { ...(pd.truck || {}), id: truckId, plate: truckPlate } } : pd
          );
          return { ...l, pickupTruck: { plate: truckPlate }, pickupDeliveries: nextPDs };
        });
        return { ...prev, [transportsessionId]: { ...session, loads: nextLoads } };
      });

      setBulkTruckAssignModalOpen(false);
      setBulkTruckAssignLoad(null);
    } catch (error) {
      alert(t("loads.errors.bulkTruckPickup"));
      console.error(error);
    }
  };

  // Bulk assign DRIVER (pickup)
  const handleBulkDriverAssign = async (
    loadId,
    driverId,
    transportsessionId,
    driverName,
    driverSlot = 1
  ) => {
    try {
      await axios.put("/pickupdeliveries/assign-driver-to-all-pickups", {
        loadId: Number(loadId),
        driverId: Number(driverId),
        driverSlot,
      });

      const safeName = (driverName || "").trim() || t("loads.common.driver");

      setLoads((prev) =>
        prev.map((l) => {
          if (l.id !== loadId) return l;
          const nextPDs = (l.pickupDeliveries || []).map((pd) =>
          pd.type === "PICKUP"
            ? driverSlot === 2
              ? { ...pd, driver2Id: Number(driverId), driver2Name: safeName }
              : { ...pd, driverId: Number(driverId), driverName: safeName }
            : pd
          );
          return { ...l, pickupDriver: { name: safeName }, pickupDeliveries: nextPDs };
        })
      );

      setGroupedLoads((prev) => {
        const session = prev?.[transportsessionId];
        if (!session) return prev;

        const nextLoads = session.loads.map((l) => {
          if (l.id !== loadId) return l;
          const nextPDs = (l.pickupDeliveries || []).map((pd) =>
            pd.type === "PICKUP"
              ? driverSlot === 2
                ? { ...pd, driver2Id: Number(driverId), driver2Name: driverName ?? pd.driver2Name ?? null }
                : { ...pd, driverId: Number(driverId), driverName: driverName ?? pd.driverName ?? null }
              : pd
          );
          return { ...l, pickupDriver: { name: driverName ?? t("loads.common.driver") }, pickupDeliveries: nextPDs };
        });

        return { ...prev, [transportsessionId]: { ...session, loads: nextLoads } };
      });

      setSessionPoints((prev) => {
        const prevForSession = Array.isArray(prev[transportsessionId]) ? prev[transportsessionId] : [];
        const nextForSession = prevForSession.map((p) =>
          p.loadId === loadId && String(p.type).toUpperCase() === "PICKUP"
            ? driverSlot === 2
              ? { ...p, driver2Id: Number(driverId), driver2Name: driverName ?? p.driver2Name ?? null }
              : { ...p, driverId: Number(driverId), driverName: driverName ?? p.driverName ?? null }
            : p
        );
        if (!prevForSession.length) return prev;
        return { ...prev, [transportsessionId]: nextForSession };
      });

      setBulkDriverAssignModalOpen(false);
      setBulkDriverAssignLoad(null);
    } catch (error) {
      alert(t("loads.errors.bulkDriverPickup"));
      console.error(error);
    }
  };

  // Bulk assign (delivery)
  const handleBulkTruckAssignDelivery = async (loadId, truckId, transportsessionId, truckPlate) => {
    try {
      await axios.put("/pickupdeliveries/assign-truck-to-all-deliveries", { loadId, truckId });
      expandedRefs.current[transportsessionId]?.updateDeliveryTrucksForLoad?.(loadId, { id: truckId, plate: truckPlate });

      setLoads((prev) =>
        prev.map((l) => {
          if (l.id !== loadId) return l;
          const nextPDs = (l.pickupDeliveries || []).map((pd) =>
            pd.type === "DELIVERY" ? { ...pd, truckId, truck: { ...(pd.truck || {}), id: truckId, plate: truckPlate } } : pd
          );
          return { ...l, deliveryTruck: { plate: truckPlate }, pickupDeliveries: nextPDs };
        })
      );

      setGroupedLoads((prev) => {
        const session = prev?.[transportsessionId];
        if (!session) return prev;
        const nextLoads = session.loads.map((l) => {
          if (l.id !== loadId) return l;
          const nextPDs = (l.pickupDeliveries || []).map((pd) =>
            pd.type === "DELIVERY" ? { ...pd, truckId, truck: { ...(pd.truck || {}), id: truckId, plate: truckPlate } } : pd
          );
          return { ...l, deliveryTruck: { plate: truckPlate }, pickupDeliveries: nextPDs };
        });
        return { ...prev, [transportsessionId]: { ...session, loads: nextLoads } };
      });

      setBulkTruckAssignModalOpen(false);
      setBulkTruckAssignLoad(null);
    } catch (error) {
      alert(t("loads.errors.bulkTruckDelivery"));
      console.error(error);
    }
  };

  // Bulk assign TRAILER (pickup)
  const handleBulkTrailerAssign = async (loadId, trailerId, transportsessionId, trailerPlate) => {
    try {
      await axios.put("/pickupdeliveries/assign-trailer-to-all-pickups", { loadId, trailerId });

      // (se o child tiver método, ótimo; se não tiver, ignora)
      expandedRefs.current[transportsessionId]?.updatePickupTrailersForLoad?.(loadId, {
        id: trailerId,
        plate: trailerPlate,
      });

      setLoads((prev) =>
        prev.map((l) => {
          if (l.id !== loadId) return l;
          const nextPDs = (l.pickupDeliveries || []).map((pd) =>
            pd.type === "PICKUP"
              ? { ...pd, trailerId, trailer: { ...(pd.trailer || {}), id: trailerId, plate: trailerPlate } }
              : pd
          );
          return { ...l, pickupTrailer: { plate: trailerPlate }, pickupDeliveries: nextPDs };
        })
      );

      setGroupedLoads((prev) => {
        const session = prev?.[transportsessionId];
        if (!session) return prev;
        const nextLoads = session.loads.map((l) => {
          if (l.id !== loadId) return l;
          const nextPDs = (l.pickupDeliveries || []).map((pd) =>
            pd.type === "PICKUP"
              ? { ...pd, trailerId, trailer: { ...(pd.trailer || {}), id: trailerId, plate: trailerPlate } }
              : pd
          );
          return { ...l, pickupTrailer: { plate: trailerPlate }, pickupDeliveries: nextPDs };
        });
        return { ...prev, [transportsessionId]: { ...session, loads: nextLoads } };
      });

      setSessionPoints((prev) => {
        const prevForSession = Array.isArray(prev[transportsessionId]) ? prev[transportsessionId] : [];
        if (!prevForSession.length) return prev;
        const nextForSession = prevForSession.map((p) =>
          p.loadId === loadId && String(p.type).toUpperCase() === "PICKUP"
            ? { ...p, trailerId, trailerPlate }
            : p
        );
        return { ...prev, [transportsessionId]: nextForSession };
      });

      setBulkTrailerAssignModalOpen(false);
      setBulkTrailerAssignLoad(null);
    } catch (error) {
    const msg =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      `${error?.response?.status || ""}`.trim() ||
      error?.message ||
      "Unknown error";

    alert(t("loads.errors.bulkTrailerPickup", `❌ Failed to assign trailer to all pickups (${msg})`));
    console.error("[BulkTrailerPickup] error:", error?.response || error);
    }
  };

  // Bulk assign TRAILER (delivery)
  const handleBulkTrailerAssignDelivery = async (loadId, trailerId, transportsessionId, trailerPlate) => {
    try {
      await axios.put("/pickupdeliveries/assign-trailer-to-all-deliveries", { loadId, trailerId });

      expandedRefs.current[transportsessionId]?.updateDeliveryTrailersForLoad?.(loadId, {
        id: trailerId,
        plate: trailerPlate,
      });

      setLoads((prev) =>
        prev.map((l) => {
          if (l.id !== loadId) return l;
          const nextPDs = (l.pickupDeliveries || []).map((pd) =>
            pd.type === "DELIVERY"
              ? { ...pd, trailerId, trailer: { ...(pd.trailer || {}), id: trailerId, plate: trailerPlate } }
              : pd
          );
          return { ...l, deliveryTrailer: { plate: trailerPlate }, pickupDeliveries: nextPDs };
        })
      );

      setGroupedLoads((prev) => {
        const session = prev?.[transportsessionId];
        if (!session) return prev;
        const nextLoads = session.loads.map((l) => {
          if (l.id !== loadId) return l;
          const nextPDs = (l.pickupDeliveries || []).map((pd) =>
            pd.type === "DELIVERY"
              ? { ...pd, trailerId, trailer: { ...(pd.trailer || {}), id: trailerId, plate: trailerPlate } }
              : pd
          );
          return { ...l, deliveryTrailer: { plate: trailerPlate }, pickupDeliveries: nextPDs };
        });
        return { ...prev, [transportsessionId]: { ...session, loads: nextLoads } };
      });

      setSessionPoints((prev) => {
        const prevForSession = Array.isArray(prev[transportsessionId]) ? prev[transportsessionId] : [];
        if (!prevForSession.length) return prev;
        const nextForSession = prevForSession.map((p) =>
          p.loadId === loadId && String(p.type).toUpperCase() === "DELIVERY"
            ? { ...p, trailerId, trailerPlate }
            : p
        );
        return { ...prev, [transportsessionId]: nextForSession };
      });

      setBulkTrailerAssignModalOpen(false);
      setBulkTrailerAssignLoad(null);
    } catch (error) {
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        `${error?.response?.status || ""}`.trim() ||
        error?.message ||
        "Unknown error";

      alert(t("loads.errors.bulkTrailerDelivery", `❌ Failed to assign trailer to all deliveries (${msg})`));
      console.error("[BulkTrailerDelivery] error:", error?.response || error);
    }
  };

  // Bulk assign DRIVER (delivery)
  const handleBulkDriverAssignDelivery = async (
    loadId,
    driverId,
    transportsessionId,
    driverName,
    driverSlot = 1
  ) => {
    try {
      await axios.put("/pickupdeliveries/assign-driver-to-all-deliveries", {
        loadId: Number(loadId),
        driverId: Number(driverId),
        driverSlot,
      });

      const safeName = (driverName || "").trim() || t("loads.common.driver");

      setLoads((prev) =>
        prev.map((l) => {
          if (l.id !== loadId) return l;
          const nextPDs = (l.pickupDeliveries || []).map((pd) =>
            pd.type === "DELIVERY"
            ? driverSlot === 2
              ? { ...pd, driver2Id: Number(driverId), driver2Name: safeName }
              : { ...pd, driverId: Number(driverId), driverName: safeName }
            : pd
          );
          return { ...l, deliveryDriver: { name: safeName }, pickupDeliveries: nextPDs };
        })
      );

      setGroupedLoads((prev) => {
        const session = prev?.[transportsessionId];
        if (!session) return prev;

        const nextLoads = session.loads.map((l) => {
          if (l.id !== loadId) return l;
          const nextPDs = (l.pickupDeliveries || []).map((pd) =>
            pd.type === "DELIVERY"
              ? driverSlot === 2
                ? { ...pd, driver2Id: Number(driverId), driver2Name: driverName ?? pd.driver2Name ?? null }
                : { ...pd, driverId: Number(driverId), driverName: driverName ?? pd.driverName ?? null }
              : pd
          );
          return { ...l, deliveryDriver: { name: driverName ?? t("loads.common.driver") }, pickupDeliveries: nextPDs };
        });

        return { ...prev, [transportsessionId]: { ...session, loads: nextLoads } };
      });

      setSessionPoints((prev) => {
        const prevForSession = Array.isArray(prev[transportsessionId]) ? prev[transportsessionId] : [];
        const nextForSession = prevForSession.map((p) =>
          p.loadId === loadId && String(p.type).toUpperCase() === "DELIVERY"
            ? driverSlot === 2
              ? { ...p, driver2Id: Number(driverId), driver2Name: driverName ?? p.driver2Name ?? null }
              : { ...p, driverId: Number(driverId), driverName: driverName ?? p.driverName ?? null }
            : p
        );
        if (!prevForSession.length) return prev;
        return { ...prev, [transportsessionId]: nextForSession };
      });

      setBulkDriverAssignModalOpen(false);
      setBulkDriverAssignLoad(null);
    } catch (error) {
      alert(t("loads.errors.bulkDriverDelivery"));
      console.error(error);
    }
  };

  useEffect(() => {
    if (selectedView !== "current") return;

    const rootEl = scrollRef.current;
    const el = loadsSentinelRef.current;
    if (!el || !rootEl) return;

    const root = rootEl;

    let raf = 0;
    const io = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (!first?.isIntersecting) return;
        if (!hasMoreCurrent) return;

        // ✅ evita disparos repetidos enquanto "carrega"
        if (loadingMoreRef.current) return;
        loadingMoreRef.current = true;
        setIsLoadingMore(true);

        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          setVisibleRowBudget((prev) => {
            const next = prev + LOADS_PAGE_SIZE;
            return Math.min(next, totalVisibleLoadCountAvailable || next);
          });
        });
      },
      { root, rootMargin: "200px 0px", threshold: 0 }
    );

    io.observe(el);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      io.disconnect();
    };
  }, [
    selectedView,
    hasMoreCurrent,
    totalVisibleLoadCountAvailable,
  ]);

  useEffect(() => {
    const onOptimisticDelete = (e) => {
      const id = e?.detail?.id;
      if (!id) return;

      // exemplo: se tens state `loads`
      setLoads((prev) => prev.filter((l) => Number(l.id) !== Number(id)));
    };

    window.addEventListener("loads:deleted:optimistic", onOptimisticDelete);
    return () => window.removeEventListener("loads:deleted:optimistic", onOptimisticDelete);
  }, []);

if (!ready) {
  return (
    <div className="loads-container">
      <div className="loads-loading" role="status" aria-live="polite">
        A carregar...
      </div>
    </div>
  );
}

return (
  <div className="loads-container">
      {showToast && (
        <div
          className="load-toast"
          style={{
            position: "fixed",
            top: 16,
            right: 16,
            background: "#10b981",
            color: "white",
            padding: "10px 14px",
            borderRadius: 8,
            boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
            zIndex: 9999,
            fontSize: 13,
            fontWeight: 500,
          }}
          role="status"
          aria-live="polite"
        >
          {toastMessage}
        </div>
      )}

      {/* Toolbar/card em 3 zonas (alinhado ao Clients) */}
      <div className="loads-header loads-card loads-toolbar" role="region" aria-label={t("loads.aria.toolbar")}>
        {/* LEFT: Stats pills */}
        <div className="loads-toolbar__left">
          <div className="loads-stats" aria-live="polite">
            <span className="loads-pill">
              {t("loads.toolbar.total")} <strong>{totalCount}</strong>
            </span>
            <span className="loads-dot">•</span>
            <span className="loads-pill">
              {t("loads.toolbar.showing")} <strong>{visibleCount}</strong>
            </span>
          </div>
        </div>

        {/* CENTER: Toggle */}
        <div className="loads-toolbar__center">
          <div
            className="loads-toggle-wrapper"
            role="tablist"
            aria-label={t("loads.aria.viewTabs")}
          >
            <div
              className="loads-toggle-slider"
              data-view={selectedView}
              aria-hidden="true"
            ></div>

            <button
              className={`loads-toggle-pill ${selectedView === "pendent" ? "active" : ""}`}
              onClick={() => handleToggle("pendent")}
              role="tab"
              aria-selected={selectedView === "pendent"}
              aria-controls="loads-panel-pendent"
              id="loads-tab-pendent"
              type="button"
            >
              {t("loads.views.pendent")}
            </button>

            <button
              className={`loads-toggle-pill ${selectedView === "current" ? "active" : ""}`}
              onClick={() => handleToggle("current")}
              role="tab"
              aria-selected={selectedView === "current"}
              aria-controls="loads-panel-current"
              id="loads-tab-current"
              type="button"
            >
              {t("loads.views.current")}
            </button>

            <button
              className={`loads-toggle-pill ${selectedView === "history" ? "active" : ""}`}
              onClick={() => handleToggle("history")}
              role="tab"
              aria-selected={selectedView === "history"}
              aria-controls="loads-panel-history"
              id="loads-tab-history"
              type="button"
            >
              {t("loads.views.history")}
            </button>
          </div>
        </div>

        {/* RIGHT: Search + Create */}
        <div className="loads-toolbar__right">
          <div className="loads-search-container">
            <FaSearch className="loads-search-icon" aria-hidden="true" />
            <input
              type="text"
              placeholder={t("loads.search.placeholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`loads-search-bar ${isFocused.search ? "input-focus" : ""}`}
              onFocus={() => setIsFocused({ ...isFocused, search: true })}
              onBlur={() => setIsFocused({ ...isFocused, search: false })}
              aria-label={t("loads.search.aria")}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="loads-clear-button"
                aria-label={t("loads.search.clearAria")}
                title={t("loads.search.clearTitle")}
                type="button"
              >
                ✕
              </button>
            )}
          </div>

          {selectedView === "pendent" && (
            <button
              className="create-load-btn"
              onClick={() => navigate("/pendentload/create")}
              type="button"
              aria-label={t("loads.actions.createNewPendentAria")}
            >
              {t("loads.actions.newLoad")}
            </button>
          )}
          {selectedView === "current" && (
            <button
              className="create-load-btn"
              onClick={() => navigate("/loads/create")}
              type="button"
              aria-label={t("loads.actions.createNewCurrentAria")}
            >
              {t("loads.actions.newLoad")}
            </button>
          )}
          {selectedView === "history" && <div style={{ width: "115px" }} aria-hidden="true"></div>}
        </div>
      </div>

      {errorMessage && <p className="error-message">{errorMessage}</p>}

      {/* PANELS */}
      {selectedView === "pendent" ? (
        <div id="loads-panel-pendent" role="tabpanel" aria-labelledby="loads-tab-pendent">
          <React.Suspense fallback={<div className="loads-loading-more">A carregar...</div>}>
            <PendentLoads
              refreshLoads={refreshLoads}
              searchTerm={searchTerm}
              searchFields={searchFieldsByView.pendent}
            />
          </React.Suspense>
        </div>
      ) : selectedView === "history" ? (
        <div id="loads-panel-history" role="tabpanel" aria-labelledby="loads-tab-history">
          <React.Suspense fallback={<div className="loads-loading-more">A carregar histórico...</div>}>
            <LoadHistory
              searchTerm={searchTerm}
              searchFields={searchFieldsByView.history}
              onVisibleCountChange={setHistoryVisibleCount}
              onReverted={(restoredLoad) => {
                showSuccessToast(
                  restoredLoad?.id
                    ? t("loads.toast.restoredWithId", { id: restoredLoad.id })
                    : t("loads.toast.restored")
                );
                handleToggle("current");
                refreshLoads("history-reverted");
              }}
            />
          </React.Suspense>
        </div>
) : baseTableRendered ? (
  <div
    className="loads-table-wrapper"
    id="loads-panel-current"
    role="tabpanel"
    aria-labelledby="loads-tab-current"
  >
    {!hasCurrentVisibleResults ? (
      <div className="loads-empty">
        <div className="loads-empty__title">
          {hasLoadsSearch && hasCurrentLoads
            ? t("loads.empty.noSearchResultsTitle", {
                defaultValue: "Nenhuma carga encontrada",
              })
            : t("loads.empty.currentTitle", {
                defaultValue: "Ainda não existem cargas ativas",
              })}
        </div>

        <div className="loads-empty__subtitle">
          {hasLoadsSearch && hasCurrentLoads
            ? t("loads.empty.noSearchResultsSubtitle", {
                defaultValue:
                  "Experimenta pesquisar por outro local, cliente, transportador ou número de carga.",
              })
            : t("loads.empty.currentSubtitle", {
                defaultValue:
                  "Cria a primeira carga para começar a acompanhar recolhas, entregas e sessões de transporte.",
              })}
        </div>

        {hasLoadsSearch && hasCurrentLoads ? (
          <button
            className="create-load-btn loads-empty__button"
            onClick={() => setSearchTerm("")}
            type="button"
          >
            {t("loads.empty.clearSearch", {
              defaultValue: "Limpar pesquisa",
            })}
          </button>
        ) : (
          <button
            className="create-load-btn loads-empty__button"
            onClick={() => navigate("/loads/create")}
            type="button"
          >
            {t("loads.actions.newLoad")}
          </button>
        )}
      </div>
    ) : (
      <>
        {/* STICKY HEADER */}
        <div className="loads-table-header" role="rowgroup" aria-label={t("loads.aria.currentTableHeader")}>
          <div className="loads-table-header-item">{t("loads.table.headers.loadNumber")}</div>
          <div className="loads-table-header-item">{t("loads.table.headers.length")}</div>
          <div className="loads-table-header-item">
            {t("loads.table.headers.truck")}<br />
            {t("loads.table.headers.pickupSuffix")}
          </div>
          <div className="loads-table-header-item">
            {t("loads.table.headers.trailer")}<br />
            {t("loads.table.headers.pickupSuffix")}
          </div>
          <div className="loads-table-header-item">
            {t("loads.table.headers.driver")}<br />
            {t("loads.table.headers.pickupSuffix")}
          </div>
          <div className="loads-table-header-item">{t("loads.table.headers.pickup")}</div>
          <div className="loads-table-header-item">{t("loads.table.headers.delivery")}</div>
          <div className="loads-table-header-item">
            {t("loads.table.headers.truck")}<br />
            {t("loads.table.headers.deliverySuffix")}
          </div>
          <div className="loads-table-header-item">
            {t("loads.table.headers.trailer")}<br />
            {t("loads.table.headers.deliverySuffix")}
          </div>
          <div className="loads-table-header-item">
            {t("loads.table.headers.driver")}<br />
            {t("loads.table.headers.deliverySuffix")}
          </div>
          <div className="loads-table-header-item">{t("loads.table.headers.client")}</div>
          <div className="loads-table-header-item">{t("loads.table.headers.carrier")}</div>
            <div className="loads-table-header-item loads-price-header">
              <span className="loads-price-header-label">
                {t("loads.table.headers.price")}
              </span>

              <button
                type="button"
                className={`loads-price-visibility-btn ${pricesHidden ? "is-hidden" : ""}`}
                title={
                  pricesHidden
                    ? t("loads.priceVisibility.show", "Mostrar preços")
                    : t("loads.priceVisibility.hide", "Ofuscar preços")
                }
                aria-label={
                  pricesHidden
                    ? t("loads.priceVisibility.show", "Mostrar preços")
                    : t("loads.priceVisibility.hide", "Ofuscar preços")
                }
                onClick={(e) => {
                  e.stopPropagation();
                  setPricesHidden((prev) => !prev);
                }}
              >
                {pricesHidden ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          <div className="loads-table-header-item">{t("loads.table.headers.actions")}</div>
        </div>

        {/* SCROLLABLE PART */}
        <div
          className="loads-table-container"
          ref={scrollRef}
          onScroll={handleLoadsScroll}
        >
          <div className="loads-table" role="rowgroup">
            {baseTableRendered &&
              visibleCurrentEntries.map((entry, index) => {
                  const {
                    transportsessionId,
                    sessionData,
                    filteredLoads,
                    shouldRenderSessionShell,
                    remainingLength,
                    tsLabel,
                  } = entry;

                  const isSessionClosing = !!closingSessions[transportsessionId];
                  const isSessionVisuallyOpen =
                    !!expandedSessions[transportsessionId] && !isSessionClosing;

                  const shouldRenderTransportSession =
                    !!expandedSessions[transportsessionId] || isSessionClosing;

                  // (extra-safe) se por algum motivo vier vazio e sem shell, não renderiza
                  if ((filteredLoads?.length || 0) === 0 && !shouldRenderSessionShell) return null;

                    return (
                      <React.Fragment key={transportsessionId}>
                        {index > 0 && <div className="session-ribbon"></div>}
                        <div className={`session-wrapper ${index > 0 ? "rounded-top-session" : ""}`}>
                          {/* Session header row — compact premium layout */}
                          <div className="loads-session-row">
                            <button
                              className="session-toggle-btn"
                              type="button"
                              onClick={() => toggleExpandSession(transportsessionId)}
                              aria-expanded={isSessionVisuallyOpen}
                              aria-label={t("loads.session.title", { number: tsLabel })}
                            >
                              <FaChevronDown
                                className={`chevron-icon ${isSessionVisuallyOpen ? "rotated" : ""}`}
                                aria-hidden="true"
                              />

                              <span
                                className="session-id-text"
                                title={t("loads.session.titleTooltip", {
                                  number: tsLabel,
                                  id: transportsessionId,
                                })}
                              >
                                {t("loads.session.title", { number: tsLabel })}
                              </span>

                              <span className="session-load-count-badge">
                                {t("loads.session.loadCount", {
                                  count: sessionData.loads?.length || 0,
                                  defaultValue:
                                    (sessionData.loads?.length || 0) === 1
                                      ? "{{count}} carga"
                                      : "{{count}} cargas",
                                })}
                              </span>

                              {selectedView === "current" &&
                                (filteredLoads?.length || 0) === 0 &&
                                activeStrategicSessions.has(transportsessionId) && (
                                  <span className="session-note">
                                    {t("loads.session.noCurrentLoadsBut")}{" "}
                                    <strong>{t("loads.session.strategicPointsPending")}</strong>.
                                  </span>
                                )}
                            </button>

                            <div className="session-header-actions">
                              <div className="session-progress session-progress--in-header">
                                <div
                                  className="session-progress-bar"
                                  aria-label={t("loads.aria.sessionProgressBar")}
                                >
                                  <div
                                    className={`session-progress-fill ${
                                      progressAnimated[transportsessionId] ? "animate" : ""
                                    }`}
                                    style={{
                                      width: `${sessionProgress[transportsessionId] ?? 0}%`,
                                    }}
                                  />
                                </div>

                                <div
                                  className="session-progress-label"
                                  aria-label={t("loads.aria.sessionProgressPct")}
                                >
                                  {sessionProgress[transportsessionId] ?? 0}%
                                </div>
                              </div>

                              {baseTableRendered &&
                                (() => {
                                  const loadBearerWithLengthSpace = sessionData.loads.find(
                                    (load) => {
                                      if (!load.loadBearer) return false;

                                      const bearerLength = parseFloat(load.length || 0);
                                      if (isNaN(bearerLength)) return false;

                                      const totalSuperimposedLength = sessionData.loads
                                        .filter(
                                          (l) =>
                                            l.associatedLoadBearerId === load.id
                                        )
                                        .reduce(
                                          (acc, l) =>
                                            acc + parseFloat(l.length || 0),
                                          0
                                        );

                                      return totalSuperimposedLength < bearerLength;
                                    }
                                  );

                                  if (
                                    remainingLength <= 0 &&
                                    !loadBearerWithLengthSpace
                                  ) {
                                    return null;
                                  }

                                  return (
                                    <div className="session-add-actions">
                                      {remainingLength > 0 && (
                                        <button
                                          className="add-load-btn add-load-btn--header"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(
                                              `/loads/create?transportsessionId=${transportsessionId}`
                                            );
                                          }}
                                          type="button"
                                          aria-label={t("loads.actions.addLoadAria", {
                                            sessionNumber: tsLabel,
                                            remaining: remainingLength.toFixed(2),
                                          })}
                                        >
                                          <FaBox className="btn-icon" />

                                          <span>
                                            {t("loads.actions.addLoadMax", {
                                              max: remainingLength.toFixed(2),
                                            })}
                                          </span>
                                        </button>
                                      )}

                                      {loadBearerWithLengthSpace && (
                                        <button
                                          className="add-superImposable-load-btn add-superImposable-load-btn--header"
                                          onClick={(e) => {
                                            e.stopPropagation();

                                            handleAddSuperImposableLoad(
                                              transportsessionId,
                                              sessionData.loads
                                            );
                                          }}
                                          type="button"
                                          aria-label={t(
                                            "loads.actions.addSuperImposableAria"
                                          )}
                                          title={t("loads.actions.addSuperImposable")}
                                        >
                                          <FaLayerGroup className="btn-icon" />

                                          <span className="session-superimposable-label">
                                            {t("loads.actions.addSuperImposable")}
                                          </span>
                                        </button>
                                      )}
                                    </div>
                                  );
                                })()}
                            </div>
                          </div>

                          {/* Expanded locations */}
                          <div
                            ref={(el) => {
                              if (el) expandedSectionRefs.current[transportsessionId] = el;
                              else delete expandedSectionRefs.current[transportsessionId];
                            }}
                            className={`expanded-section-wrapper ${
                              isSessionVisuallyOpen ? "expanded" : "collapsed"
                            } ${isSessionClosing ? "closing" : ""}`}
                          >
                            <div
                              ref={(el) => {
                                if (el) expandedInnerRefs.current[transportsessionId] = el;
                                else delete expandedInnerRefs.current[transportsessionId];
                              }}
                              className="expanded-inner"
                            >
                              {shouldRenderTransportSession && baseTableRendered && (
                                <React.Suspense fallback={<div className="loads-loading-more">A carregar sessão…</div>}>
                                  <TransportSessionLocations
                                    ref={(ref) => {
                                      if (ref) expandedRefs.current[transportsessionId] = ref;
                                      else delete expandedRefs.current[transportsessionId];
                                    }}
                                    transportsessionId={transportsessionId}
                                    transportSessionNumber={groupedLoads[transportsessionId]?.tsUiCounter ?? null}
                                    refreshSignal={reloadTransportSessionKey}
                                    isLoading={loadingSessions[transportsessionId] === true}
                                    refreshLoads={refreshLoads}
                                    loads={sessionData.loads}
                                    setPendingCmrUploads={setPendingCmrUploads}
                                    openCmrModal={(loadId, pointContext) => openCmrModalAndFetch(loadId, pointContext)}
                                    onCmrFinalized={() => {
                                      showSuccessToast(t("loads.toast.completedNowInHistory"));
                                      refreshSessionStrategics(transportsessionId);
                                    }}
                                    onPointsChanged={(updatedPoints) =>
                                      refreshSessionStrategics(transportsessionId, updatedPoints)
                                    }
                                    frozenLoadIdsForSession={(() => {
                                      const hasCurrent = sessionData.loads.some((l) => l.status === "current");
                                      if (!hasCurrent) return [];
                                      return sessionData.loads
                                        .filter((l) => l.status === "completed" || l.status === "cancelled")
                                        .map((l) => l.id);
                                    })()}
                                    freezeStrategicPoints={!sessionData.loads.some((l) => l.status === "current")}
                                    isClosingSession={isSessionClosing}
                                  />
                                </React.Suspense>
                              )}
                            </div>
                          </div>

                        {/* Loads in session */}
                        {filteredLoads.map((load, i, arr) => {
                          const isLast = i === arr.length - 1;
                          const pointsForSession = sessionPoints[transportsessionId];

                          // =========================================================
                          // Location metadata
                          // Source priority:
                          // 1. PickupDelivery loaded for this session
                          // 2. denormalized Load summary fields
                          // =========================================================

                          const pointsForThisLoad =
                            Array.isArray(pointsForSession)
                              ? pointsForSession.filter(
                                  (point) =>
                                    Number(point?.loadId) ===
                                    Number(load?.id)
                                )
                              : [];

                          const sortLocationPoints = (
                            a,
                            b
                          ) => {
                            const seqA =
                              Number(
                                a?.seq ??
                                  a?.sequence
                              );

                            const seqB =
                              Number(
                                b?.seq ??
                                  b?.sequence
                              );

                            if (
                              Number.isFinite(seqA) &&
                              Number.isFinite(seqB) &&
                              seqA !== seqB
                            ) {
                              return seqA - seqB;
                            }

                            const timeA =
                              `${a?.date || "9999-12-31"}T${a?.hour || "23:59"}`;

                            const timeB =
                              `${b?.date || "9999-12-31"}T${b?.hour || "23:59"}`;

                            return timeA.localeCompare(
                              timeB
                            );
                          };

                          const pickupPoint =
                            pointsForThisLoad
                              .filter(
                                (point) =>
                                  String(
                                    point?.type || ""
                                  ).toUpperCase() ===
                                  "PICKUP"
                              )
                              .sort(
                                sortLocationPoints
                              )[0] ||
                            null;

                          const deliveryPointsForLoad =
                            pointsForThisLoad
                              .filter(
                                (point) =>
                                  String(
                                    point?.type || ""
                                  ).toUpperCase() ===
                                  "DELIVERY"
                              )
                              .sort(
                                sortLocationPoints
                              );

                          const deliveryPoint =
                            deliveryPointsForLoad[0] ||
                            null;

                          const pickupLocationMeta = {
                            city:
                              pickupPoint?.city ??
                              pickupPoint?.place?.city ??
                              load?.pickupCity ??
                              "",

                            postalCode:
                              pickupPoint?.postalCode ??
                              pickupPoint?.place?.postalCode ??
                              load?.pickupPostalCode ??
                              "",

                            country:
                              pickupPoint?.country ??
                              pickupPoint?.place?.country ??
                              load?.pickupCountry ??
                              "",
                          };

                          const deliveryLocationMeta = {
                            city:
                              deliveryPoint?.city ??
                              deliveryPoint?.place?.city ??
                              load?.deliveryCity ??
                              "",

                            postalCode:
                              deliveryPoint?.postalCode ??
                              deliveryPoint?.place?.postalCode ??
                              load?.deliveryPostalCode ??
                              "",

                            country:
                              deliveryPoint?.country ??
                              deliveryPoint?.place?.country ??
                              load?.deliveryCountry ??
                              "",
                          };

                          // Pick-Up plates/label
                          const pickupPlates = Array.isArray(pointsForSession)
                            ? platesFromPoints(pointsForSession, load.id, "PICKUP")
                            : [];
                          const pickupPrimaryLabel =
                            pickupPlates[0] ||
                            load.pickupTruck?.plate || // fallback from summary field
                            t("loads.common.allPickups");
                          const pickupExtraCount = Math.max(0, pickupPlates.length - 1);

                          // Delivery plates/label
                          const deliveryPlates = Array.isArray(pointsForSession)
                            ? platesFromPoints(pointsForSession, load.id, "DELIVERY")
                            : [];
                          const deliveryPrimaryLabel =
                            (deliveryPlates.length ? deliveryPlates[deliveryPlates.length - 1] : null) ||
                            load.deliveryTruck?.plate ||
                            t("loads.common.allDeliveries");
                          const deliveryExtraCount = Math.max(0, deliveryPlates.length - 1);

                          // Trailer plates/label (Pick-Up)
                          const pickupTrailerPlates = Array.isArray(pointsForSession)
                            ? trailerPlatesFromPoints(pointsForSession, load.id, "PICKUP")
                            : [];
                          const pickupTrailerPrimaryLabel =
                            pickupTrailerPlates[0] ||
                            load.pickupTrailer?.plate ||
                            (load.trailerPickupInfo ? String(load.trailerPickupInfo).split("•")[0].trim() : "") ||
                            t("loads.common.allPickups");
                          const pickupTrailerExtraCount = Math.max(0, pickupTrailerPlates.length - 1);

                          // Trailer plates/label (Delivery)
                          const deliveryTrailerPlates = Array.isArray(pointsForSession)
                            ? trailerPlatesFromPoints(pointsForSession, load.id, "DELIVERY")
                            : [];
                          const deliveryTrailerPrimaryLabel =
                            (deliveryTrailerPlates.length ? deliveryTrailerPlates[deliveryTrailerPlates.length - 1] : null) ||
                            load.deliveryTrailer?.plate ||
                            (load.trailerDeliveryInfo ? String(load.trailerDeliveryInfo).split("•")[0].trim() : "") ||
                            t("loads.common.allDeliveries");
                          const deliveryTrailerExtraCount = Math.max(0, deliveryTrailerPlates.length - 1);

                          // Driver names (Pick-Up)
                          const pickupDrivers = Array.isArray(pointsForSession)
                            ? driverNamesFromPoints(pointsForSession, load.id, "PICKUP")
                            : [];

                          const normalizeSummaryDriverLabel = (value) => {
                            const label = String(value || "").trim();

                            // Avoid showing generic backend summaries like "2 drivers" as the main label.
                            if (/^\d+\s+drivers?$/i.test(label)) return "";

                            return label;
                          };

                          const pickupDriverName = normalizeSummaryDriverLabel(
                            load.pickupDriver?.name || load.driverPickupInfo || ""
                          );

                          const pickupDriverPrimary =
                            pickupDrivers[0] ||
                            pickupDriverName ||
                            (load.driverIdPickup ? `#${load.driverIdPickup}` : t("loads.common.allPickups"));

                          const pickupDriverExtraCount = Math.max(0, pickupDrivers.length - 1);

                          // Driver names (Delivery)
                          const deliveryDrivers = Array.isArray(pointsForSession)
                            ? driverNamesFromPoints(pointsForSession, load.id, "DELIVERY")
                            : [];

                          const deliveryDriverName = normalizeSummaryDriverLabel(
                            load.deliveryDriver?.name || load.driverDeliveryInfo || ""
                          );

                          const deliveryDriverPrimary =
                            deliveryDrivers[0] ||
                            deliveryDriverName ||
                            (load.driverIdDelivery ? `#${load.driverIdDelivery}` : t("loads.common.allDeliveries"));

                          const deliveryDriverExtraCount = Math.max(0, deliveryDrivers.length - 1);

                          // (optional) use real counts for badges next to pickup/delivery locations
                          const pickupCount = Array.isArray(pointsForSession)
                            ? countPointsOfType(pointsForSession, load.id, "PICKUP")
                            : load.pickupDeliveries?.filter?.((l) => l.type === "PICKUP").length || 0;

                          const deliveryCount = Array.isArray(pointsForSession)
                            ? countPointsOfType(pointsForSession, load.id, "DELIVERY")
                            : load.pickupDeliveries?.filter?.((l) => l.type === "DELIVERY").length || 0;

                          const pickupDateLabel =
                            load.pickupDate
                              ? formatLoadLocationDate(
                                  load.pickupDate,
                                  load.pickupHour,
                                  priceLocale
                                )
                              : t(
                                  "loads.common.noDate"
                                );

                          const deliveryDateLabel =
                            load.deliveryDate
                              ? formatLoadLocationDate(
                                  load.deliveryDate,
                                  load.deliveryHour,
                                  priceLocale
                                )
                              : t(
                                  "loads.common.noDate"
                                );

                          return (
                            <div
                              key={load.id}
                              data-load-id={load.id}
                              className={[
                                "loads-table-row",
                                "clickable",

                                isLast
                                  ? "last-row-in-session"
                                  : "",

                                Number(
                                  load.id
                                ) ===
                                Number(
                                  focusedLoadId
                                )
                                  ? "loads-table-row--focused"
                                  : "",
                              ]
                                .filter(
                                  Boolean
                                )
                                .join(
                                  " "
                                )}
                              onClick={() => handleEditLoad(load.id)}
                              role="row"
                              tabIndex={0}
                              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleEditLoad(load.id)}
                              aria-label={t("loads.aria.editLoad", { loadNumber: load.uiCounter })}
                            >

                              {/* Load # + Shipment Request */}
                              <div className="loads-table-item">
                                <div className="loads-load-id-stack">
                                  <span className="loads-load-id-primary">
                                    {load.uiCounter}
                                  </span>

                                  {Number(load.shipmentRequestId) > 0 && (
                                    <span
                                      className="loads-shipment-request-id"
                                      title={`Shipment Request #${load.shipmentRequestId}`}
                                    >
                                      REQ-{String(load.shipmentRequestId).padStart(6, "0")}
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Length */}
                              <div className="loads-table-item">
                                {load.length}m
                              </div>

                              {/* Truck (Pick-Up) */}
                              <div className="loads-table-item">
                                <button
                                  className={`loads-chip ${
                                    hasAssignedTruckBulk(pointsForSession, load, "PICKUP") ? "loads-chip-assigned-truck" : ""
                                  }`}
                                  data-variant="pickup"
                                  title={t("loads.chips.assignTruckAllPickupsTitle")}
                                  aria-label={t("loads.chips.assignTruckAllPickupsAria", { loadNumber: load.uiCounter })}
                                  onClick={async (e) => {
                                    e.stopPropagation();

                                    let points = sessionPoints[transportsessionId];
                                    if (!Array.isArray(points)) {
                                      try {
                                        const { data } = await axios.get(
                                          `/pickupdeliveries/${transportsessionId}/light?sort=seq&t=${Date.now()}`
                                        );
                                        points = data;
                                      } catch (err) {
                                        console.error("Failed to fetch points for session", transportsessionId, err);
                                        alert(t("loads.alerts.couldNotFetchSessionPoints"));
                                        return;
                                      }
                                    }

                                    const allPickups = points.filter((p) => p.loadId === load.id && p.type === "PICKUP");
                                    const allDeliveries = points.filter((p) => p.loadId === load.id && p.type === "DELIVERY");

                                    if (allPickups.length === 0) {
                                      alert(t("loads.alerts.noPickupPoints"));
                                      return;
                                    }

                                    React.startTransition(() => {
                                      setBulkTruckAssignLoad({
                                        ...load,
                                        transportsessionId, // ✅
                                        bulkType: "pickup", // ou delivery
                                        allPickups,
                                        allDeliveries,
                                        isExternalCarrier: isExternalTransportCarrier(load), // ✅ passa a flag, se quiseres
                                      });
                                      setBulkTruckAssignModalOpen(true);
                                    });
                                  }}
                                  type="button"
                                >
                                  <ResourceChipContent
                                    icon={Truck}
                                    iconClassName="truck-chip-icon"
                                    label={pickupPrimaryLabel}
                                    extraCount={pickupExtraCount}
                                  />
                                </button>
                              </div>

                              {/* Trailer (Pick-Up) */}
                              <div className="loads-table-item">
                                <button
                                  className={`loads-chip ${
                                    hasAssignedTrailerBulk(pointsForSession, load, "PICKUP") ? "loads-chip-assigned-trailer" : ""
                                  }`}
                                  data-variant="pickup"
                                  title={t("loads.chips.assignTrailerAllPickupsTitle", "Assign trailer to all pickups")}
                                  aria-label={t("loads.chips.assignTrailerAllPickupsAria", { loadNumber: load.uiCounter })}
                                  onClick={async (e) => {
                                    e.stopPropagation();

                                    // (se quiseres também bloquear external carrier aqui, diz-me e fazemos free-text trailer)
                                    let points = sessionPoints[transportsessionId];
                                    if (!Array.isArray(points)) {
                                      try {
                                        const { data } = await axios.get(
                                          `/pickupdeliveries/${transportsessionId}/light?sort=seq&t=${Date.now()}`
                                        );
                                        points = data;
                                      } catch (err) {
                                        console.error("Failed to fetch points for session", transportsessionId, err);
                                        alert(t("loads.alerts.couldNotFetchSessionPoints"));
                                        return;
                                      }
                                    }

                                    const allPickups = points.filter((p) => p.loadId === load.id && p.type === "PICKUP");
                                    const allDeliveries = points.filter((p) => p.loadId === load.id && p.type === "DELIVERY");

                                    if (allPickups.length === 0) {
                                      alert(t("loads.alerts.noPickupPoints"));
                                      return;
                                    }

                                    React.startTransition(() => {
                                      setBulkTrailerAssignLoad({
                                        ...load,
                                        transportsessionId, // ✅ obrigatório
                                        bulkType: "pickup",
                                        allPickups,
                                        allDeliveries,
                                        isExternalCarrier: isExternalTransportCarrier(load), // ✅ igual ao truck/driver
                                      });
                                      setBulkTrailerAssignModalOpen(true);
                                    });
                                  }}
                                  type="button"
                                >
                                  <ResourceChipContent
                                    icon={Container}
                                    iconClassName="truck-chip-icon"
                                    label={pickupTrailerPrimaryLabel}
                                    extraCount={pickupTrailerExtraCount}
                                  />
                                </button>
                              </div>

                              {/* Driver (Pick-Up) */}
                              <div className="loads-table-item">
                                <button
                                  className={`loads-chip ${
                                    hasAssignedDriverBulk(pointsForSession, load, "PICKUP") ? "loads-chip-assigned-driver" : ""
                                  }`}
                                  data-variant="pickup"
                                  title={t("loads.chips.assignDriverAllPickupsTitle")}
                                  aria-label={t("loads.chips.assignDriverAllPickupsAria", { loadNumber: load.uiCounter })}
                                  onClick={async (e) => {
                                    e.stopPropagation();

                                    // Internal carrier -> normal bulk driver modal (assign by driverId)
                                    let points = sessionPoints[transportsessionId];
                                    if (!Array.isArray(points)) {
                                      try {
                                        const { data } = await axios.get(
                                          `/pickupdeliveries/${transportsessionId}/light?sort=seq&t=${Date.now()}`
                                        );
                                        points = data;
                                      } catch (err) {
                                        console.error("Failed to fetch points for session", transportsessionId, err);
                                        alert(t("loads.alerts.couldNotFetchSessionPoints"));
                                        return;
                                      }
                                    }

                                    const allPickups = points.filter((p) => p.loadId === load.id && p.type === "PICKUP");
                                    const allDeliveries = points.filter((p) => p.loadId === load.id && p.type === "DELIVERY");

                                    if (allPickups.length === 0) {
                                      alert(t("loads.alerts.noPickupPoints"));
                                      return;
                                    }

                                    React.startTransition(() => {
                                      setBulkDriverAssignLoad({
                                        ...load,
                                        transportsessionId, // ✅ IMPORTANT
                                        bulkType: "pickup",
                                        allPickups,
                                        allDeliveries,
                                        isExternalCarrier: isExternalTransportCarrier(load), // ✅ CRITICAL (ativa quickMode)
                                      });
                                      setBulkDriverAssignModalOpen(true);
                                    });
                                  }}
                                  type="button"
                                >
                                  <ResourceChipContent
                                    icon={User}
                                    iconClassName="driver-chip-icon"
                                    label={pickupDriverPrimary}
                                    extraCount={pickupDriverExtraCount}
                                  />
                                </button>
                              </div>

                              {/* Pick-Up info */}
                              <div className="loads-table-item loads-location-cell">
                                <LoadLocationSummary
                                  location={
                                    load.pickupLocation ||
                                    pickupPoint?.location ||
                                    pickupPoint?.place?.placeName
                                  }
                                  city={
                                    pickupLocationMeta.city
                                  }
                                  postalCode={
                                    pickupLocationMeta.postalCode
                                  }
                                  country={
                                    pickupLocationMeta.country
                                  }
                                  dateLabel={
                                    pickupDateLabel
                                  }
                                  extraCount={
                                    Math.max(
                                      0,
                                      pickupCount - 1
                                    )
                                  }
                                  variant="pickup"
                                  fallback={
                                    t(
                                      "loads.common.na"
                                    )
                                  }
                                />
                              </div>

                              {/* Delivery info */}
                              <div className="loads-table-item loads-location-cell">
                                <LoadLocationSummary
                                  location={
                                    load.deliveryLocation ||
                                    deliveryPoint?.location ||
                                    deliveryPoint?.place?.placeName
                                  }
                                  city={
                                    deliveryLocationMeta.city
                                  }
                                  postalCode={
                                    deliveryLocationMeta.postalCode
                                  }
                                  country={
                                    deliveryLocationMeta.country
                                  }
                                  dateLabel={
                                    deliveryDateLabel
                                  }
                                  extraCount={
                                    Math.max(
                                      0,
                                      deliveryCount - 1
                                    )
                                  }
                                  variant="delivery"
                                  fallback={
                                    t(
                                      "loads.common.na"
                                    )
                                  }
                                />
                              </div>

                              {renderLoadTypeBadges(load, t)}

                              {/* Truck (Delivery) */}
                              <div className="loads-table-item">
                                <button
                                  className={`loads-chip ${
                                    hasAssignedTruckBulk(pointsForSession, load, "DELIVERY") ? "loads-chip-assigned-truck" : ""
                                  }`}
                                  data-variant="delivery"
                                  title={t("loads.chips.assignTruckAllDeliveriesTitle")}
                                  aria-label={t("loads.chips.assignTruckAllDeliveriesAria", { loadNumber: load.uiCounter })}
                                  onClick={async (e) => {
                                    e.stopPropagation();

                                    dbgTruck("[Loads][DeliveryTruckChip] click", {
                                      transportsessionId,
                                      loadId: load?.id,
                                      carrierId: load?.carrierId,
                                    });

                                    dbgTruck("[Loads][DeliveryTruckChip] routing -> TruckSelectorModal (bulk)", {
                                      loadId: load?.id,
                                      transportsessionId,
                                    });

                                    let points = sessionPoints[transportsessionId];
                                    if (!Array.isArray(points)) {
                                      try {
                                        const { data } = await axios.get(
                                          `/pickupdeliveries/${transportsessionId}/light?sort=seq&t=${Date.now()}`
                                        );
                                        points = data;
                                      } catch (err) {
                                        console.error("Failed to fetch points for session", transportsessionId, err);
                                        alert(t("loads.alerts.couldNotFetchSessionPoints"));
                                        return;
                                      }
                                    }

                                    const allPickups = points.filter((p) => p.loadId === load.id && p.type === "PICKUP");
                                    const allDeliveries = points.filter((p) => p.loadId === load.id && p.type === "DELIVERY");

                                    if (allDeliveries.length === 0) {
                                      alert(t("loads.alerts.noDeliveryPoints"));
                                      return;
                                    }

                                    React.startTransition(() => {
                                      setBulkTruckAssignLoad({
                                        ...load,
                                        transportsessionId, // ✅
                                        bulkType: "delivery", // ou delivery
                                        allPickups,
                                        allDeliveries,
                                        isExternalCarrier: isExternalTransportCarrier(load), // ✅ passa a flag, se quiseres
                                      });
                                      setBulkTruckAssignModalOpen(true);
                                    });
                                  }}
                                  type="button"
                                >
                                  <ResourceChipContent
                                    icon={Truck}
                                    iconClassName="truck-chip-icon"
                                    label={deliveryPrimaryLabel}
                                    extraCount={deliveryExtraCount}
                                  />
                                </button>
                              </div>

                              {/* Trailer (Delivery) */}
                              <div className="loads-table-item">
                                <button
                                  className={`loads-chip ${
                                    hasAssignedTrailerBulk(pointsForSession, load, "DELIVERY") ? "loads-chip-assigned-trailer" : ""
                                  }`}
                                  data-variant="delivery"
                                  title={t("loads.chips.assignTrailerAllDeliveriesTitle", "Assign trailer to all deliveries")}
                                  aria-label={t("loads.chips.assignTrailerAllDeliveriesAria", { loadNumber: load.uiCounter })}
                                  onClick={async (e) => {
                                    e.stopPropagation();

                                    let points = sessionPoints[transportsessionId];
                                    if (!Array.isArray(points)) {
                                      try {
                                        const { data } = await axios.get(
                                          `/pickupdeliveries/${transportsessionId}/light?sort=seq&t=${Date.now()}`
                                        );
                                        points = data;
                                      } catch (err) {
                                        console.error("Failed to fetch points for session", transportsessionId, err);
                                        alert(t("loads.alerts.couldNotFetchSessionPoints"));
                                        return;
                                      }
                                    }

                                    const allPickups = points.filter((p) => p.loadId === load.id && p.type === "PICKUP");
                                    const allDeliveries = points.filter((p) => p.loadId === load.id && p.type === "DELIVERY");

                                    if (allDeliveries.length === 0) {
                                      alert(t("loads.alerts.noDeliveryPoints"));
                                      return;
                                    }

                                    React.startTransition(() => {
                                      setBulkTrailerAssignLoad({
                                        ...load,
                                        transportsessionId, // ✅
                                        bulkType: "delivery",
                                        allPickups,
                                        allDeliveries,
                                        isExternalCarrier: isExternalTransportCarrier(load), // ✅
                                      });
                                      setBulkTrailerAssignModalOpen(true);
                                    });
                                  }}
                                  type="button"
                                >
                                  <ResourceChipContent
                                    icon={Container}
                                    iconClassName="truck-chip-icon"
                                    label={deliveryTrailerPrimaryLabel}
                                    extraCount={deliveryTrailerExtraCount}
                                  />
                                </button>
                              </div>

                              {/* Driver (Delivery) */}
                              <div className="loads-table-item">
                                <button
                                  className={`loads-chip ${
                                    hasAssignedDriverBulk(pointsForSession, load, "DELIVERY") ? "loads-chip-assigned-driver" : ""
                                  }`}
                                  data-variant="delivery"
                                  title={t("loads.chips.assignDriverAllDeliveriesTitle")}
                                  aria-label={t("loads.chips.assignDriverAllDeliveriesAria", { loadNumber: load.uiCounter })}
                                  onClick={async (e) => {
                                    e.stopPropagation();

                                    let points = sessionPoints[transportsessionId];
                                    if (!Array.isArray(points)) {
                                      try {
                                        const { data } = await axios.get(
                                          `/pickupdeliveries/${transportsessionId}/light?sort=seq&t=${Date.now()}`
                                        );
                                        points = data;
                                      } catch (err) {
                                        console.error("Failed to fetch points for session", transportsessionId, err);
                                        alert(t("loads.alerts.couldNotFetchSessionPoints"));
                                        return;
                                      }
                                    }

                                    const allPickups = points.filter((p) => p.loadId === load.id && p.type === "PICKUP");
                                    const allDeliveries = points.filter((p) => p.loadId === load.id && p.type === "DELIVERY");

                                    if (allDeliveries.length === 0) {
                                      alert(t("loads.alerts.noDeliveryPoints"));
                                      return;
                                    }

                                    React.startTransition(() => {
                                      setBulkDriverAssignLoad({
                                        ...load,
                                        transportsessionId, // ✅ IMPORTANT
                                        bulkType: "delivery",
                                        allPickups,
                                        allDeliveries,
                                        isExternalCarrier: isExternalTransportCarrier(load), // ✅ CRITICAL
                                      });
                                      setBulkDriverAssignModalOpen(true);
                                    });
                                  }}
                                  type="button"
                                >
                                  <ResourceChipContent
                                    icon={User}
                                    iconClassName="driver-chip-icon"
                                    label={deliveryDriverPrimary}
                                    extraCount={deliveryDriverExtraCount}
                                  />
                                </button>
                              </div>

                              {/* Client */}
                              <div className="loads-table-item">
                                {(load.clientType === "industry" ||
                                  load.industryClientName) &&
                                  load.industryClientName && (
                                    <div className="client-info loads-entity-info">
                                      <span
                                        className="loads-entity-icon loads-entity-icon--client"
                                        title={t(
                                          "loads.clients.industryTitle"
                                        )}
                                      >
                                        <Building2
                                          aria-hidden="true"
                                        />
                                      </span>

                                      <span title={load.industryClientName}>
                                        {load.industryClientName}
                                      </span>
                                    </div>
                                  )}

                                {(load.clientType === "carrier" ||
                                  load.carrierClientName) &&
                                  load.carrierClientName && (
                                    <div className="client-info loads-entity-info">
                                      <span
                                        className="loads-entity-icon loads-entity-icon--client"
                                        title={t(
                                          "loads.clients.carrierClientTitle"
                                        )}
                                      >
                                        <Building2
                                          aria-hidden="true"
                                        />
                                      </span>

                                      <span title={load.carrierClientName}>
                                        {load.carrierClientName}
                                      </span>
                                    </div>
                                  )}

                                {!load.industryClientName &&
                                  !load.carrierClientName && (
                                    <div className="client-info">
                                      <span>—</span>
                                    </div>
                                  )}
                              </div>

                              {/* Carrier */}
                              <div className="loads-table-item">
                                {load.carrierName ? (
                                  <div className="client-info loads-entity-info">
                                    <span
                                      className="loads-entity-icon loads-entity-icon--carrier"
                                      title={t(
                                        "loads.clients.transportCarrierTitle"
                                      )}
                                    >
                                      <Truck
                                        aria-hidden="true"
                                      />
                                    </span>

                                    <span title={load.carrierName}>
                                      {load.carrierName}
                                    </span>
                                  </div>
                                ) : (
                                  t(
                                    "loads.common.na"
                                  )
                                )}
                              </div>

                              {/* Price */}
                              <div className="loads-table-item loads-price-cell">
                                <span className={`loads-price-value ${pricesHidden ? "is-hidden" : ""}`}>
                                  {pricesHidden ? "••••••" : formatPrice(load.price, { locale: priceLocale })}
                                </span>
                              </div>

                              {/* Actions */}
                              <div className="loads-table-item loads-actions-cell">
                                <div className="trash-wrapper">
                                  {(() => {
                                    const loadNoteText = String(
                                      load.notes ?? localStorage.getItem(`loadNote:${load.id}`) ?? ""
                                    ).trim();

                                    const hasLoadNotes = loadNoteText.length > 0;

                                    return (
                                      <button
                                        type="button"
                                        className={`note-inline-btn ${hasLoadNotes ? "has-notes" : ""}`}
                                        title={hasLoadNotes ? "" : t("loads.notes.title")}
                                        aria-label={t("loads.notes.openAria", { loadNumber: load.uiCounter })}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openNotesModal(load);
                                        }}
                                      >
                                        <FaRegStickyNote />

                                        {hasLoadNotes && (
                                          <span className="load-note-tooltip" role="tooltip">
                                            <span className="load-note-tooltip__title">
                                              {t("loads.notes.tooltipTitle", "Nota da carga")}
                                            </span>

                                            <span className="load-note-tooltip__text">
                                              {loadNoteText}
                                            </span>
                                          </span>
                                        )}
                                      </button>
                                    );
                                  })()}

                                  <button
                                    className="icon-btn danger"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemove(load.id);
                                    }}
                                    title={t("loads.actions.removeTitle")}
                                    aria-label={t("loads.actions.removeAria", { loadNumber: load.uiCounter })}
                                    type="button"
                                  >
                                    <FaTrashAlt />
                                  </button>

                                  {pendingCmrUploads.has(load.id) && (
                                    <button
                                      className="icon-btn upload-wrapper"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const pointContext = Array.isArray(pointsForSession)
                                          ? getLastDeliveryPointContextForLoad(load.id, pointsForSession)
                                          : null;

                                        openCmrModalAndFetch(load.id, pointContext);
                                      }}
                                      title={t("loads.actions.uploadCmrTitle")}
                                      aria-label={t("loads.actions.uploadCmrAria", { loadNumber: load.uiCounter })}
                                      type="button"
                                    >
                                      <FaUpload />
                                      <span className="cmr-badge" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </React.Fragment>
                );
              })}
              {selectedView === "current" && hasMoreCurrent && (
                <>
                  <div
                    ref={loadsSentinelRef}
                    style={{ height: 1 }}
                    aria-hidden="true"
                  />
                  {isLoadingMore && (
                    <div className="loads-loading-more" role="status" aria-live="polite">
                      {t("loads.common.loadingMore", "A carregar...")}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  ) : (
    <div className="loads-loading" role="status" aria-live="polite">
      {t("loads.common.loadingTable")}
    </div>
  )}

      {/* Free-text truck modal (external carriers) */}
      {freeTextTruckModalOpen && freeTextTruckCtx?.load && (
        <React.Suspense fallback={null}>
          <FreeTextTruckModal
            isOpen={freeTextTruckModalOpen}
            onClose={() => {
              setFreeTextTruckModalOpen(false);
              setFreeTextTruckCtx(null);
            }}
          title={
            freeTextTruckCtx.bulkType === "delivery"
              ? t("loads.modals.freeTextTruck.deliveryTitle", { id: freeTextTruckCtx.load.id })
              : t("loads.modals.freeTextTruck.pickupTitle", { id: freeTextTruckCtx.load.id })
          }
          initialValue={
            freeTextTruckCtx.bulkType === "delivery"
              ? (freeTextTruckCtx.load?.truckDeliveryInfo ? String(freeTextTruckCtx.load.truckDeliveryInfo).split("•")[0].trim() : "")
              : (freeTextTruckCtx.load?.truckPickupInfo ? String(freeTextTruckCtx.load.truckPickupInfo).split("•")[0].trim() : "")
          }
          maxLen={15}
          lettersOnly={true}
          onSubmit={async (value) => {
            const loadId = freeTextTruckCtx.load.id;
            const cleaned = String(value || "").trim().slice(0, 15);
            const payload =
              freeTextTruckCtx.bulkType === "delivery"
                ? { truckDeliveryInfo: `${cleaned} • manual` }
                : { truckPickupInfo: `${cleaned} • manual` };

            // save server
            await axios.put(`/loads/${loadId}`, payload);

            // optimistic UI update (table labels use pickupTruck/deliveryTruck fallbacks)
            setLoads((prev) =>
              prev.map((l) => {
                if (l.id !== loadId) return l;
                if (freeTextTruckCtx.bulkType === "delivery") {
                  return {
                    ...l,
                    truckDeliveryInfo: payload.truckDeliveryInfo,
                    deliveryTruck: { plate: cleaned },
                  };
                }
                return {
                  ...l,
                  truckPickupInfo: payload.truckPickupInfo,
                  pickupTruck: { plate: cleaned },
                };
              })
            );

            setGroupedLoads((prev) => {
              const sid = String(freeTextTruckCtx.transportsessionId);
              const session = prev?.[sid];
              if (!session) return prev;
              const nextLoads = session.loads.map((l) => {
                if (l.id !== loadId) return l;
                if (freeTextTruckCtx.bulkType === "delivery") {
                  return {
                    ...l,
                    truckDeliveryInfo: payload.truckDeliveryInfo,
                    deliveryTruck: { plate: cleaned },
                  };
                }
                return {
                  ...l,
                  truckPickupInfo: payload.truckPickupInfo,
                  pickupTruck: { plate: cleaned },
                };
              });
              return { ...prev, [sid]: { ...session, loads: nextLoads } };
            });
            // ✅ IMPORTANT: close modal + clear ctx (igual ao driver)
            setFreeTextTruckModalOpen(false);
            setFreeTextTruckCtx(null);
            }}
          />
        </React.Suspense>
      )}


      {/* Free-text driver modal (external carriers) */}
      {freeTextDriverModalOpen && freeTextDriverCtx?.load && (
        <React.Suspense fallback={null}>
          <FreeTextDriverModal
            isOpen={freeTextDriverModalOpen}
            onClose={() => {
              setFreeTextDriverModalOpen(false);
              setFreeTextDriverCtx(null);
            }}
          title={
            freeTextDriverCtx.bulkType === "delivery"
              ? t("loads.modals.freeTextDriver.deliveryTitle", { id: freeTextDriverCtx.load.id })
              : t("loads.modals.freeTextDriver.pickupTitle", { id: freeTextDriverCtx.load.id })
          }
          initialValue={
            freeTextDriverCtx.bulkType === "delivery"
              ? (freeTextDriverCtx.load?.driverDeliveryInfo ? String(freeTextDriverCtx.load.driverDeliveryInfo).split("•")[0].trim() : "")
              : (freeTextDriverCtx.load?.driverPickupInfo ? String(freeTextDriverCtx.load.driverPickupInfo).split("•")[0].trim() : "")
          }
          maxLen={40}
          onSubmit={async (cleanedName) => {
            const loadId = freeTextDriverCtx.load.id;
            const cleaned = String(cleanedName || "").trim().slice(0, 40);

            const payload =
              freeTextDriverCtx.bulkType === "delivery"
                ? { driverDeliveryInfo: `${cleaned} • manual` }
                : { driverPickupInfo: `${cleaned} • manual` };

            // save server
            await axios.put(`/loads/${loadId}`, payload);

            // optimistic UI update
            setLoads((prev) =>
              prev.map((l) => {
                if (l.id !== loadId) return l;
                if (freeTextDriverCtx.bulkType === "delivery") {
                  return { ...l, driverDeliveryInfo: payload.driverDeliveryInfo, deliveryDriver: { name: cleaned } };
                }
                return { ...l, driverPickupInfo: payload.driverPickupInfo, pickupDriver: { name: cleaned } };
              })
            );

            setGroupedLoads((prev) => {
              const sid = String(freeTextDriverCtx.transportsessionId);
              const session = prev?.[sid];
              if (!session) return prev;

              const nextLoads = session.loads.map((l) => {
                if (l.id !== loadId) return l;
                if (freeTextDriverCtx.bulkType === "delivery") {
                  return { ...l, driverDeliveryInfo: payload.driverDeliveryInfo, deliveryDriver: { name: cleaned } };
                }
                return { ...l, driverPickupInfo: payload.driverPickupInfo, pickupDriver: { name: cleaned } };
              });

              return { ...prev, [sid]: { ...session, loads: nextLoads } };
            });

            // ✅ IMPORTANT: close the modal + clear ctx
            setFreeTextDriverModalOpen(false);
            setFreeTextDriverCtx(null);
            }}
          />
        </React.Suspense>
      )}

      {/* Truck assign modal */}
      {bulkTruckAssignModalOpen && bulkTruckAssignLoad && (
        <React.Suspense fallback={null}>
          <TruckSelectorModal
          isOpen={bulkTruckAssignModalOpen}
          mode="bulk"
          onClose={() => {
            setBulkTruckAssignModalOpen(false);
            setBulkTruckAssignLoad(null);
          }}
          onSelect={async (truck) => {
            if (bulkTruckAssignLoad.bulkType === "delivery") {
              await handleBulkTruckAssignDelivery(
                bulkTruckAssignLoad.id,
                truck.id,
                bulkTruckAssignLoad.transportsessionId,
                truck.plate
              );
            } else {
              await handleBulkTruckAssign(
                bulkTruckAssignLoad.id,
                truck.id,
                bulkTruckAssignLoad.transportsessionId,
                truck.plate
              );
            }
          }}
          title={
            bulkTruckAssignLoad.bulkType === "delivery"
              ? t("loads.modals.bulkTruck.deliveryTitle", { id: bulkTruckAssignLoad.id })
              : t("loads.modals.bulkTruck.pickupTitle", { id: bulkTruckAssignLoad.id })
          }
          currentLoad={bulkTruckAssignLoad}
          setIsTruckUpdating={() => {}}
          onTruckAssigned={() => {}}
          selectedType={bulkTruckAssignLoad.bulkType || "pickup"}
          allPickups={bulkTruckAssignLoad.allPickups}
          allDeliveries={bulkTruckAssignLoad.allDeliveries}
        />
        </React.Suspense>
      )}

      {/* Trailer assign modal */}
      {bulkTrailerAssignModalOpen && bulkTrailerAssignLoad && (
        <React.Suspense fallback={null}>
          <TrailerSelectorModal
            isOpen={bulkTrailerAssignModalOpen}
            mode="bulk"
            onClose={() => {
              setBulkTrailerAssignModalOpen(false);
              setBulkTrailerAssignLoad(null);
            }}
            onSelect={async (trailer) => {
            const isManual = trailer?.isManual === true || (!trailer?.id && !!trailer?.trailerPlate);

            // tenta ser compatível com trailer.plate ou trailer.trailerNumber
            const plate =
              (trailer?.plate || trailer?.trailerPlate || trailer?.trailerNumber || "").trim() ||
              t("loads.common.trailer", "Trailer");

            // ✅ QUICK/MANUAL: o modal já fez o PUT no backend, aqui só atualizamos UI
            if (isManual) {
              const loadId = bulkTrailerAssignLoad?.id;
              const sid = String(bulkTrailerAssignLoad?.transportsessionId);

              const denorm =
                bulkTrailerAssignLoad?.bulkType === "delivery"
                  ? { trailerIdDelivery: null, trailerDeliveryInfo: `${plate} • manual`, deliveryTrailer: { plate } }
                  : { trailerIdPickup: null, trailerPickupInfo: `${plate} • manual`, pickupTrailer: { plate } };

              // optimistic update: loads list
              setLoads((prev) =>
                prev.map((l) => (l.id === loadId ? { ...l, ...denorm } : l))
              );

              // optimistic update: grouped loads
              setGroupedLoads((prev) => {
                const session = prev?.[sid];
                if (!session) return prev;
                return {
                  ...prev,
                  [sid]: {
                    ...session,
                    loads: session.loads.map((l) => (l.id === loadId ? { ...l, ...denorm } : l)),
                  },
                };
              });

              // optional: seed sessionPoints so chips also show immediately (if points already loaded)
              setSessionPoints((prev) => {
                const rows = Array.isArray(prev?.[sid]) ? prev[sid] : null;
                if (!rows) return prev;
                const typeUpper = String(bulkTrailerAssignLoad?.bulkType || "pickup").toUpperCase(); // "PICKUP" | "DELIVERY"
                const next = rows.map((p) =>
                  Number(p.loadId) === Number(loadId) && String(p.type || "").toUpperCase() === typeUpper
                    ? { ...p, trailerId: null, trailerPlate: plate }
                    : p
                );
                return { ...prev, [sid]: next };
              });

              setBulkTrailerAssignModalOpen(false);
              setBulkTrailerAssignLoad(null);
              return;
            }

            // ✅ normal mode: precisa de trailer.id
            if (!trailer?.id) return;

            if (bulkTrailerAssignLoad.bulkType === "delivery") {
              await handleBulkTrailerAssignDelivery(
                bulkTrailerAssignLoad.id,
                trailer.id,
                bulkTrailerAssignLoad.transportsessionId,
                plate
              );
            } else {
              await handleBulkTrailerAssign(
                bulkTrailerAssignLoad.id,
                trailer.id,
                bulkTrailerAssignLoad.transportsessionId,
                plate
              );
            }
          }}
          title={
            bulkTrailerAssignLoad.bulkType === "delivery"
              ? t("loads.modals.bulkTrailer.deliveryTitle", { id: bulkTrailerAssignLoad.id })
              : t("loads.modals.bulkTrailer.pickupTitle", { id: bulkTrailerAssignLoad.id })
          }
          currentLoad={bulkTrailerAssignLoad}
          selectedType={bulkTrailerAssignLoad.bulkType || "pickup"}
          allPickups={bulkTrailerAssignLoad.allPickups}
          allDeliveries={bulkTrailerAssignLoad.allDeliveries}
          />
        </React.Suspense>
      )}

      {bulkDriverAssignModalOpen && bulkDriverAssignLoad && (
        <React.Suspense fallback={null}>
          <DriverSelectorModal
            isOpen={bulkDriverAssignModalOpen}
            mode="bulk"
            driverSlot={bulkDriverSlot}
            onClose={() => {
              setBulkDriverAssignModalOpen(false);
              setBulkDriverAssignLoad(null);
            }}
            onSelect={async (driver) => {
              if (!driver?.id) return;
              const displayName = (driver.name || driver.fullName || driver.nickname || t("loads.common.driverWithId", { id: driver.id })).trim();

              if (bulkDriverAssignLoad.bulkType === "delivery") {
                await handleBulkDriverAssignDelivery(
                  bulkDriverAssignLoad.id,
                  Number(driver.id),
                  bulkDriverAssignLoad.transportsessionId,
                  displayName,
                  bulkDriverSlot
                );
              } else {
                await handleBulkDriverAssign(
                  bulkDriverAssignLoad.id,
                  Number(driver.id),
                  bulkDriverAssignLoad.transportsessionId,
                  displayName,
                  bulkDriverSlot
                );
              }
            }}
            title={
              bulkDriverAssignLoad.bulkType === "delivery"
                ? t("loads.modals.bulkDriver.deliveryTitle", { id: bulkDriverAssignLoad.id })
                : t("loads.modals.bulkDriver.pickupTitle", { id: bulkDriverAssignLoad.id })
            }
            currentLoad={bulkDriverAssignLoad}
            selectedType={bulkDriverAssignLoad.bulkType || "pickup"}
            allPickups={bulkDriverAssignLoad.allPickups}
            allDeliveries={bulkDriverAssignLoad.allDeliveries}
          />
        </React.Suspense>
      )}

      {/* CMR upload modal */}
      {cmrModalOpen && selectedLoadForCmr && (
        <CMRUploadModal
          key={`cmr-${selectedLoadForCmr}-${existingCmrUrls.length}-${cmrUploadContext?.pickupDeliveryId || "no-point"}`}
          isOpen={cmrModalOpen}
          loadId={selectedLoadForCmr}
          pickupDeliveryId={cmrUploadContext?.pickupDeliveryId ?? null}
          pointLabel={cmrUploadContext?.pointLabel ?? null}
          pointLocation={cmrUploadContext?.pointLocation ?? null}
          existingFiles={existingCmrUrls}
          onClose={() => {
            console.log("[Loads] CMR modal close", { selectedLoadForCmr });
            if (selectedLoadForCmr) {
              setPendingCmrUploads((prev) => new Set(prev).add(selectedLoadForCmr));
            }
            setCmrModalOpen(false);
            setSelectedLoadForCmr(null);
            setCmrUploadContext(null);
            setExistingCmrUrls([]);
          }}
          onUpload={async (files, context = {}) => {
            const activeLoadId = context.loadId ?? selectedLoadForCmr;

            const activePickupDeliveryId =
              context.pickupDeliveryId ??
              cmrUploadContext?.pickupDeliveryId ??
              null;

            console.log("[Loads] onUpload start", {
              filesCount: files?.length,
              loadId: activeLoadId,
              pickupDeliveryId: activePickupDeliveryId,
              context,
              cmrUploadContext,
            });

            try {
              if (files.length > 0) {
                const formData = new FormData();

                formData.append("loadId", activeLoadId);

                if (activePickupDeliveryId) {
                  formData.append("pickupDeliveryId", activePickupDeliveryId);
                }

                files.forEach((f) => formData.append("loadDocPhotos", f));

                const uploadRes = await axios.post(`/loads/upload/loadDocPhotos`, formData, {
                  headers: { "Content-Type": "multipart/form-data" },
                });

                console.log("[Loads] upload response", uploadRes?.data);
              }

              const finRes = await axios.post(`/loads/${activeLoadId}/finalize-cmr`);

              console.log("[Loads] finalize response", finRes?.data);

              setPendingCmrUploads((prev) => {
                const next = new Set(prev);
                next.delete(activeLoadId);
                return next;
              });

              setCmrModalOpen(false);
              setSelectedLoadForCmr(null);
              setCmrUploadContext(null);
              setExistingCmrUrls([]);

              showSuccessToast(t("loads.toast.completedNowInHistory"));
              await refreshLoads("cmr-finalized");
            } catch (err) {
              console.error("[Loads] onUpload error", err);
              alert(t("loads.errors.cmrUploadFinalizeFailed"));
            }
          }}
        />
      )}

      {/* Notes modal */}
      {notesModalOpen && (
        <React.Suspense fallback={null}>
          <NotesModal
            isOpen={notesModalOpen}
            load={notesModalLoad}
            defaultValue={notesDefaultValue}
            onClose={closeNotesModal}
            onSave={saveNotesFromModal}
          />
        </React.Suspense>
      )}
    </div>
  );
}

const LOAD_COUNTRY_CODES = {
  portugal: "PT",

  espanha: "ES",
  spain: "ES",

  franca: "FR",
  france: "FR",

  alemanha: "DE",
  germany: "DE",

  italia: "IT",
  italy: "IT",

  belgica: "BE",
  belgium: "BE",

  holanda: "NL",
  "paises baixos": "NL",
  netherlands: "NL",

  luxemburgo: "LU",
  luxembourg: "LU",

  suica: "CH",
  switzerland: "CH",

  austria: "AT",

  polonia: "PL",
  poland: "PL",

  chequia: "CZ",
  czechia: "CZ",
  "republica checa": "CZ",
  "czech republic": "CZ",

  eslovaquia: "SK",
  slovakia: "SK",

  hungria: "HU",
  hungary: "HU",

  romenia: "RO",
  romania: "RO",

  bulgaria: "BG",

  eslovenia: "SI",
  slovenia: "SI",

  croacia: "HR",
  croatia: "HR",

  dinamarca: "DK",
  denmark: "DK",

  suecia: "SE",
  sweden: "SE",

  noruega: "NO",
  norway: "NO",

  finlandia: "FI",
  finland: "FI",

  irlanda: "IE",
  ireland: "IE",

  "reino unido": "GB",
  "united kingdom": "GB",
  "great britain": "GB",
  uk: "GB",

  grecia: "GR",
  greece: "GR",

  lituania: "LT",
  lithuania: "LT",

  letonia: "LV",
  latvia: "LV",

  estonia: "EE",

  servia: "RS",
  serbia: "RS",
};

function normalizeLoadCountry(value) {
  return String(value || "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function getLoadCountryCode(country) {
  const normalized =
    normalizeLoadCountry(country);

  if (
    /^[a-z]{2}$/.test(
      normalized
    )
  ) {
    return normalized.toUpperCase();
  }

  return (
    LOAD_COUNTRY_CODES[
      normalized
    ] ||
    null
  );
}

function LoadCountryFlag({
  country,
}) {
  const code =
    getLoadCountryCode(
      country
    );

  const Flag =
    code
      ? CountryFlags[
          code
        ]
      : null;

  if (!Flag) {
    return null;
  }

  return (
    <Flag
      className="loads-country-flag"
      aria-hidden="true"
    />
  );
}

function buildLoadAreaLabel({
  city,
  postalCode,
  country,
}) {
  const locality =
    String(
      city ||
        postalCode ||
        ""
    ).trim();

  const countryLabel =
    String(
      country ||
        ""
    ).trim();

  return [
    locality,
    countryLabel,
  ]
    .filter(Boolean)
    .join(", ");
}

function formatLoadLocationDate(
  date,
  hour,
  locale
) {
  if (!date) {
    return "";
  }

  let dateLabel = "";

  const rawDate =
    String(date)
      .slice(
        0,
        10
      );

  const match =
    rawDate.match(
      /^(\d{4})-(\d{2})-(\d{2})$/
    );

  if (match) {
    const [
      ,
      year,
      month,
      day,
    ] = match;

    const localDate =
      new Date(
        Number(year),
        Number(month) - 1,
        Number(day)
      );

    dateLabel =
      localDate.toLocaleDateString(
        locale,
        {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }
      );
  } else {
    const parsed =
      new Date(date);

    dateLabel =
      Number.isNaN(
        parsed.getTime()
      )
        ? rawDate
        : parsed.toLocaleDateString(
            locale,
            {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            }
          );
  }

  const cleanHour =
    String(
      hour ||
        ""
    )
      .trim()
      .slice(
        0,
        5
      );

  return cleanHour
    ? `${dateLabel} · ${cleanHour}`
    : dateLabel;
}

function LoadLocationSummary({
  location,
  city,
  postalCode,
  country,
  dateLabel,
  extraCount = 0,
  variant,
  fallback,
}) {
  const areaLabel =
    buildLoadAreaLabel({
      city,
      postalCode,
      country,
    });

  return (
    <div className="loads-location-summary">
      <div className="loads-location-name-row">
        <span className="loads-location-name">
          {location || fallback}
        </span>

        {extraCount > 0 && (
          <span
            className={[
              "location-badge",
              "loads-location-extra-badge",
              variant === "pickup"
                ? "location-badge-floating-pickup"
                : "location-badge-floating-delivery",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            +{extraCount}
          </span>
        )}
      </div>

      {areaLabel && (
        <div className="loads-location-meta">
          <LoadCountryFlag
            country={country}
          />

          <MapPin
            className="loads-location-pin"
            aria-hidden="true"
          />

          <span className="loads-location-area">
            {areaLabel}
          </span>
        </div>
      )}

      <div className="loads-location-date">
        {dateLabel}
      </div>
    </div>
  );
}

export default Loads;